import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../firebaseInit';
import { confirmDialog } from '../utils/dialogHelper';
import { scriptGraph } from '../scriptGraph';
import TemplateSelector from './shared/TemplateSelector';
import UserInviteForm from './shared/UserInviteForm';
import TeamForm from './shared/TeamForm';
// TeamSelector removed - userTeam now auto-switches via AuthContext
// ReactFlow useNodesState/useEdgesState removed — flowchart is now on a separate page
import toast from 'react-hot-toast';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './shared/LoadingSpinner';
import Button from './shared/Button';
import Modal from './shared/Modal';
import Card from './shared/Card';
import PageHeader from './shared/PageHeader';
import EmptyState from './shared/EmptyState';
import { colors, spacing, typography, shadows, zIndex } from '../styles/designTokens';
import MiniFlowchartPreview from './shared/MiniFlowchartPreview';
import NodeResponsesTable from './shared/NodeResponsesTable';
import RevisionHistory from './shared/RevisionHistory';
import * as firestorePaths from '../utils/firestorePaths';
import { getArtifactProjectId } from '../utils/firestorePaths';

const ScriptBuilder = ({ onClose, onApplyChanges, onUpdateTemplate, onDeleteTemplate, onSetAsDefault, onTemplateSelectionChange, availableTemplates, currentTemplate, defaultTemplate, onUnsavedChangesChange, onAutoSaveChange, onRegisterSave, onFlowchartModalChange, refreshTemplates, user, userTeam, userTeamName, initialNodeId, teamSubscriptionPlan }) => {
  const navigate = useNavigate();
    
  // Use notification system (replaces alert())
  const { showSuccess, showError, showWarning, showInfo } = useNotification();
    
  // Get auth context for admin check and team (userTeam prop may not always be passed)
  const { userRole, userTeam: authUserTeam } = useAuth();
  const isAdmin = userRole === 'admin';
  const effectiveUserTeam = userTeam || authUserTeam;
    
  // userTeam now auto-switches when admin views another team via AuthContext
  // Templates are loaded in App.js based on viewing team - no need to load separately here
    
  // Centralized logging for Script Builder actions
  const logScriptBuilderAction = (action, details = {}) => {
    if (window.logCriticalAction) {
      window.logCriticalAction('ACTION', `Script Builder: ${action}`, {
        ...details,
        currentTemplate: selectedTemplate,
        viewMode: viewMode,
        hasUnsavedChanges: hasUnsavedChanges,
        userEmail: user?.email ? '[REDACTED]' : 'null',
        timestamp: new Date().toISOString()
      });
    } else {
      console.log(`🔧 Script Builder: ${action}`, details);
    }
  };
    
  const [localScriptGraph, setLocalScriptGraph] = useState(scriptGraph || { nodes: {}, ui: { positions: {} } });
  const [editingResponseData, setEditingResponseData] = useState({});
  const [viewMode, setViewMode] = useState('flow-connected'); // 'flow-connected', 'edit', 'add', 'tree'
  const [selectedNode, setSelectedNode] = useState(null);
  const [newNodeData, setNewNodeData] = useState({});
  const [templateName, setTemplateName] = useState(`Custom Script ${new Date().toLocaleDateString('en-US', { 
    month: '2-digit', 
    day: '2-digit', 
    year: '2-digit' 
  }).replace(/\//g, '/')}, ${new Date().toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit', 
    hour12: true 
  })}`);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(currentTemplate || defaultTemplate || 'example-script');
  const [editingSteps, setEditingSteps] = useState(false);
  const [steps, setSteps] = useState([]); // Initialize as empty, will be loaded from template
  const [prospectForm, setProspectForm] = useState({
    title: 'Lead Data',
    fields: [
      { id: 'agentName', label: 'Agent Name', type: 'text', placeholder: 'Enter your name', required: true },
      { id: 'prospectName', label: 'Contact Name', type: 'text', placeholder: 'Enter contact\'s full name', required: true },
      { id: 'company', label: 'Company', type: 'text', placeholder: 'Enter company name', required: false },
      { id: 'prospectPhone', label: 'Phone Number', type: 'tel', placeholder: 'Enter phone number', required: true }
    ]
  });
  // draggedNode and dragOverStep removed — node dragging between phases handled in accordion
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  // showInstructions and showProcessSteps removed — replaced by Advanced Settings accordion
  const [draggedResponse, setDraggedResponse] = useState(null);
  const [draggedPhase, setDraggedPhase] = useState(null);
  const [dragOverPhase, setDragOverPhase] = useState(null);
  const [templateNameError, setTemplateNameError] = useState('');
  const [showAddNodeOption, setShowAddNodeOption] = useState(null); // null = none, responseKey = which one
  const [newNodeName, setNewNodeName] = useState('');
  // Enhanced node creation modal states
  const [showEnhancedNodeCreator, setShowEnhancedNodeCreator] = useState(false);
  const [currentResponseKey, setCurrentResponseKey] = useState(null);
  const [nodeCreationData, setNodeCreationData] = useState({
    id: '',
    script: '',
    coaching: '',
    step: 'interest'
  });
  const [editingPath, setEditingPath] = useState([]); // Track editing path for breadcrumbs
  // Removed showAddDataFieldOption and newDataFieldName since data fields are now auto-generated
  const [showDeleteNodeConfirm, setShowDeleteNodeConfirm] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState(null);
    
  // Bulk node operations removed — phase assignment is done in Advanced Settings accordion
    
  // collapsedPhases, compactView, expandedNodes removed — grid view replaced by quick-access list
  // Always sort by creation time (earliest first)
    
  // Objection response management states
  const [editingObjections, setEditingObjections] = useState({}); // Track which phases are editing objections
  const [newObjectionData, setNewObjectionData] = useState({ label: '', response: '' });
  const [showAddObjection, setShowAddObjection] = useState(null); // stepId when adding objection

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [templateDirty, setTemplateDirty] = useState(false);
  const [dataFieldsUpdated, setDataFieldsUpdated] = useState(0); // Track data field updates
  const [loading, setLoading] = useState(true); // Loading state for initialization
  const [isInitialized, setIsInitialized] = useState(false); // Track if all data is loaded
  const [autoSave, setAutoSave] = useState(true);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const initGuardRef = useRef(true); // blocks autosave until first init completes
  const [showRevisionHistory, setShowRevisionHistory] = useState(false);

  // const [view, setView] = useState('script'); // Removed - flowchart is now on separate page
  const [dataFields, setDataFields] = useState([]);
  const [showDataFields, setShowDataFields] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showChangeSummary, setShowChangeSummary] = useState(false);
  const [changeSummary, setChangeSummary] = useState({ added: [], updated: [], deleted: [] });

  const [isDirty, setIsDirty] = useState(false);
  const [validationExpanded, setValidationExpanded] = useState(false); // Collapsed by default
  const [showActionsMenu, setShowActionsMenu] = useState(false); // For actions dropdown
  const [showSaveConfirm, setShowSaveConfirm] = useState(false); // For save overwrite confirmation
  
  // Node Quick-Access List states
  const [nodeSearchQuery, setNodeSearchQuery] = useState('');
  const [nodeSortBy, setNodeSortBy] = useState('name');
  
  // Advanced Settings Accordion states (kept for potential future use, collapsed by default)
  const [advancedPhasesOpen, setAdvancedPhasesOpen] = useState(false);
  const [advancedObjectionsOpen, setAdvancedObjectionsOpen] = useState(false);

  // Sub-tab state: which section is shown below the flowchart preview
  const [activeBuilderTab, setActiveBuilderTab] = useState('nodes');
    
  // Validation functions for script completeness
  const validateScriptFlow = useCallback(() => {
    if (!localScriptGraph || !localScriptGraph.nodes) {
      console.warn('⚠️ localScriptGraph or nodes is undefined:', localScriptGraph);
      return {
        startNodes: [],
        endNodes: [],
        incompleteNodes: [],
        isValid: false
      };
    }
    const nodes = localScriptGraph.nodes;
    // Filter out objection nodes from validation as they're automatically generated
    const nodeIds = Object.keys(nodes).filter(id => id && typeof id === 'string' && !id.startsWith('objection_obj_'));
        
    // Find start nodes - opening node designation ALWAYS trumps everything else
    const openingNodes = nodeIds.filter(id => nodes[id].isOpeningNode);
    let startNodes = [];
        
    if (openingNodes.length > 0) {
      // Opening node designation trumps all - ONLY use designated opening nodes
      startNodes = openingNodes;
    } else {
      // Only if NO opening node is designated, fall back to unreferenced nodes
      const referencedNodes = new Set();
      Object.values(nodes).forEach(node => {
        if (node.responses) {
          Object.values(node.responses).forEach(resp => {
            if (resp.nextNode && resp.nextNode !== '__end_script__') {
              referencedNodes.add(resp.nextNode);
            }
          });
        }
      });
      startNodes = nodeIds.filter(id => !referencedNodes.has(id));
    }
        
    // Find end nodes (nodes with responses that explicitly end the script)
    const endNodes = nodeIds.filter(id => {
      const node = nodes[id];
      if (!node.responses || Object.keys(node.responses).length === 0) return false;
      return Object.values(node.responses).some(resp => resp.nextNode === '__end_script__' || resp.isEndNode);
    });
        
    // Find orphaned nodes (no responses or incomplete responses)
    const incompleteNodes = nodeIds.filter(id => {
      const node = nodes[id];
      if (!node.responses || Object.keys(node.responses).length === 0) return true;
      return Object.values(node.responses).some(resp => !resp.nextNode);
    });
        
    // Find broken connections (responses pointing to non-existent nodes)
    // Exclude objection nodes from both source and target analysis
    const brokenConnections = [];
    nodeIds.forEach(id => {
      const node = nodes[id];
      if (node.responses) {
        Object.entries(node.responses).forEach(([responseKey, response]) => {
          if (response.nextNode && 
                        response.nextNode !== '__end_script__' && 
                        !nodes[response.nextNode] &&
                        typeof response.nextNode === 'string' && !response.nextNode.startsWith('objection_obj_')) {
            brokenConnections.push({
              nodeId: id,
              responseKey,
              missingTarget: response.nextNode,
              responseLabel: response.label || responseKey
            });
          }
        });
      }
    });
    
    // Find unreachable nodes (nodes that exist but nothing points to them)
    // These are orphans with exits but no entrance
    const unreachableNodes = [];
    if (startNodes.length > 0) {
      // Trace all reachable nodes from start
      const reachable = new Set();
      const toVisit = [...startNodes];
      
      while (toVisit.length > 0) {
        const currentId = toVisit.shift();
        if (reachable.has(currentId) || currentId === '__end_script__' || !currentId) continue;
        reachable.add(currentId);
        
        const node = nodes[currentId];
        if (node && node.responses) {
          Object.values(node.responses).forEach(resp => {
            const nextId = resp.nextNode || resp.nextNodeId;
            if (nextId && !reachable.has(nextId) && nextId !== '__end_script__') {
              toVisit.push(nextId);
            }
          });
        }
      }
      
      // Any node not in reachable set is unreachable (orphaned)
      nodeIds.forEach(id => {
        if (!reachable.has(id)) {
          unreachableNodes.push(id);
        }
      });
    }
        
    return {
      startNodes,
      endNodes, 
      incompleteNodes,
      brokenConnections,
      unreachableNodes,
      openingNodes,
      isValid: startNodes.length === 1 && endNodes.length >= 1 && incompleteNodes.length === 0 && brokenConnections.length === 0 && unreachableNodes.length === 0
    };
  }, [localScriptGraph]);
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [showNodeCreationModal, setShowNodeCreationModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalContent, setConfirmModalContent] = useState({ title: '', message: '', onConfirm: null });

  // Templates come from App.js via availableTemplates prop
  // App.js already loads the correct templates based on viewing team
  const displayTemplates = availableTemplates;

  const handleDeleteResponse = (sourceNodeId, responseKey) => {
    const newGraph = { ...localScriptGraph };
    delete newGraph.nodes[sourceNodeId].responses[responseKey];
    setLocalScriptGraph(newGraph);
    markDirty();
  };

  // togglePhaseCollapse removed — replaced by Advanced Settings accordion

  const handleEditPhasesToggle = () => {
    const newEditingState = !editingSteps;
    setEditingSteps(newEditingState);
  };

  // toggleNodeExpansion removed — grid view replaced by quick-access list

  const handleResponseKeyChange = (nodeId, oldKey, newKey) => {
    if (!newKey || oldKey === newKey) return;

    const newGraph = { ...localScriptGraph };
    const responses = newGraph.nodes[nodeId].responses;
        
    if (responses[newKey]) {
      showError(`Response key "${newKey}" already exists.`);
      return;
    }

    responses[newKey] = responses[oldKey];
    delete responses[oldKey];
        
    setLocalScriptGraph(newGraph);
    markDirty();
  };

  // Generate a unique sequential response key (response_1, response_2, ...), skipping existing keys
  const getNextResponseKey = (responsesObj) => {
    const responses = responsesObj || {};
    let index = 1;
    while (responses[`response_${index}`]) {
      index += 1;
    }
    return `response_${index}`;
  };

  // Use ref to track previous currentTemplate to prevent infinite loops
  const prevCurrentTemplateRef = useRef(currentTemplate);
  const prevDefaultTemplateRef = useRef(defaultTemplate);

  // Default steps for fallback - no default objections, users add them manually
  const defaultSteps = [
    { 
      id: 'interest', 
      name: 'Interest', 
      description: 'Ensuring there is interest to sell',
      objectionResponses: [] // Empty - users add objections manually
    },
    { 
      id: 'motivation', 
      name: 'Motivation', 
      description: 'Determining motivations to sell',
      objectionResponses: [] // Empty - users add objections manually
    },
    { 
      id: 'condition', 
      name: 'Condition', 
      description: 'Learning about house condition and call to action',
      objectionResponses: [] // Empty - users add objections manually
    }
  ];

  // Helper functions for node actions (flowchart now on separate page)
  const scrollContainerRef = useRef(null);

  const handleEditNode = (nodeId) => {
    if (!nodeId) return;
    // Always read the LATEST node data from localScriptGraph (not from closure)
    const freshNode = localScriptGraph?.nodes?.[nodeId];
    if (!freshNode) return;
        
    const safeNode = createSafeSelectedNode(freshNode, nodeId);
    safeNode.originalId = nodeId;
    setSelectedNode(safeNode);
    // Open side panel instead of switching to full-page edit mode
    // viewMode stays as-is (e.g. 'flow-connected'), node list remains visible
  };

  const handleAddResponseFromFlow = (nodeId, node) => {
    console.log('➕ Add Response button clicked for node:', nodeId);
    const safeNode = createSafeSelectedNode(node, nodeId);
    safeNode.originalId = nodeId;
    setSelectedNode(safeNode);
  };

  // Consolidated initialization useEffect to prevent flickering
  useEffect(() => {
    // Only initialize if we have the required data
    if (!currentTemplate || !displayTemplates || !displayTemplates[currentTemplate]) {
      setLoading(true);
      setIsInitialized(false);
      return;
    }

    // Determine whether we're staying on the same template
    const isSameTemplate = prevCurrentTemplateRef.current === currentTemplate;

    // Batch all initialization operations
    const template = displayTemplates[currentTemplate];
        
    console.log('📦 ScriptBuilder loading template:', {
      templateId: currentTemplate,
      hasTemplate: !!template,
      hasGraph: !!template?.graph,
      hasDataScriptGraph: !!template?.data?.scriptGraph,
      hasSteps: !!template?.steps,
      stepsCount: template?.steps?.length || 0,
      stepIds: template?.steps?.map(s => s.id) || []
    });
        
    // 1. Load template graph
    console.log('📂 Loading template graph for:', currentTemplate);
    console.log('📂 Full template object:', template);
    console.log('📂 template.graph:', template?.graph);
    console.log('📂 template.data:', template?.data);
    console.log('📂 template.data?.scriptGraph:', template?.data?.scriptGraph);
        
    // After DataContext normalization, graph should always be at template.graph
    const currentGraph = template?.graph || template?.data?.scriptGraph || scriptGraph;
        
    console.log('📂 Selected graph (after priority):', currentGraph);
    console.log('📂 Template graph ui.positions:', currentGraph.ui?.positions);
    console.log('📂 Template graph node count:', Object.keys(currentGraph?.nodes || {}).length);
    console.log('📂 Template graph node IDs:', Object.keys(currentGraph?.nodes || {}));
        
    // Additional debugging for node details
    if (currentGraph?.nodes) {
      Object.entries(currentGraph.nodes).forEach(([nodeId, nodeData]) => {
        console.log(`📂 Node "${nodeId}":`, {
          script: nodeData.script?.substring(0, 50) + '...',
          responsesCount: Object.keys(nodeData.responses || {}).length,
          step: nodeData.step,
          isOpeningNode: nodeData.isOpeningNode
        });
      });
    }
        
    // If we're updating the same template (e.g., autosave), avoid full re-init flicker
    if (isInitialized && isSameTemplate) {
      setLocalScriptGraph(currentGraph);
      setTemplateDirty(false);
    } else {
      // Full init path
      setLoading(true);
      setLocalScriptGraph(currentGraph);
      setTemplateDirty(false);
    }
        
    // 2. Set template name
    if (currentTemplate === 'example-script') {
      setTemplateName(`Custom Script ${new Date().toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: '2-digit' 
      }).replace(/\//g, '/')}, ${new Date().toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      })}`);
    } else {
      const templateName = template?.name;
      if (templateName) {
        setTemplateName(templateName);
      }
    }
        
    // 3. Load steps
    const templateSteps = template.steps;
    console.log('📋 Loading steps from template:', {
      hasSteps: !!templateSteps,
      isArray: Array.isArray(templateSteps),
      count: templateSteps?.length || 0,
      stepIds: templateSteps?.map(s => s.id) || []
    });
        
    if (templateSteps && Array.isArray(templateSteps)) {
      const migratedSteps = templateSteps.map(step => {
        if (!step.objectionResponses) {
          return { ...step, objectionResponses: [] };
        }
        return step;
      });
      setSteps(migratedSteps);
      console.log('✅ Steps loaded from template:', migratedSteps.map(s => s.id));
            
      // Auto-create missing objection nodes for all objections in the template
      let needsUpdate = false;
      const updatedGraph = { ...currentGraph, nodes: { ...currentGraph.nodes } };
            
      migratedSteps.forEach(step => {
        if (step.objectionResponses && step.objectionResponses.length > 0) {
          step.objectionResponses.forEach(objection => {
            const objectionNodeId = `objection_${objection.id}`;
            if (!updatedGraph.nodes[objectionNodeId]) {
              console.log(`🔧 Auto-creating missing objection node: ${objectionNodeId}`);
              needsUpdate = true;
              updatedGraph.nodes[objectionNodeId] = {
                id: objectionNodeId,
                script: objection.response || '',
                coaching: `Handle objection: ${objection.label || ''}`,
                responses: {
                  'continue': {
                    nextNode: '__return_to_previous__',
                    data: {},
                    label: 'Continue with question'
                  },
                  'end_call': {
                    nextNode: '__end_script__',
                    data: {},
                    label: 'End Call'
                  }
                },
                step: step.id,
                isObjectionNode: true,
                requiresTextInput: false,
                aiConfig: {
                  canGenerateCustomScript: true,
                  canAddCustomResponses: true,
                  canSkipToAnyNode: false,
                  contextWindow: 5,
                  fallbackNode: '__end_script__'
                }
              };
            }
          });
        }
      });
            
      if (needsUpdate) {
        console.log('✅ Auto-created missing objection nodes, updating graph');
        setLocalScriptGraph(updatedGraph);
        setTemplateDirty(true); // Mark as dirty so it saves the new nodes
      }
    } else {
      setSteps(defaultSteps);
      console.log('⚠️ No steps in template, using defaults:', defaultSteps.map(s => s.id));
    }
        
    // 4. Load prospect form
    const templateProspectForm = template.prospectForm;
    if (templateProspectForm) {
      setProspectForm(templateProspectForm);
    } else {
      setProspectForm({
        title: 'Lead Data',
        fields: [
          { id: 'agentName', label: 'Agent Name', type: 'text', placeholder: 'Enter your name', required: true },
          { id: 'prospectName', label: 'Prospect Name', type: 'text', placeholder: 'Enter prospect\'s full name', required: true },
          { id: 'company', label: 'Company', type: 'text', placeholder: 'Enter company name', required: false },
          { id: 'prospectPhone', label: 'Phone Number', type: 'tel', placeholder: 'Enter phone number', required: true }
        ]
      });
    }
        
    // 5. Sync selected template - update if either currentTemplate or defaultTemplate changes
    if (currentTemplate !== prevCurrentTemplateRef.current || defaultTemplate !== prevDefaultTemplateRef.current) {
      // Use currentTemplate if available, fallback to defaultTemplate, then example-script
      const newTemplate = currentTemplate || defaultTemplate || 'example-script';
      console.log('🔄 ScriptBuilder syncing selectedTemplate:', {
        from: selectedTemplate,
        to: newTemplate,
        currentTemplate,
        defaultTemplate,
        trigger: currentTemplate !== prevCurrentTemplateRef.current ? 'currentTemplate changed' : 'defaultTemplate changed'
      });
      setSelectedTemplate(newTemplate);
      prevCurrentTemplateRef.current = currentTemplate;
      prevDefaultTemplateRef.current = defaultTemplate;
    }
        
    // Mark as fully initialized with a small delay to ensure all state updates complete
    if (isInitialized && isSameTemplate) {
      // Lightweight update path: do not toggle loading to prevent flicker
      // Keep isInitialized as true
    } else {
      setTimeout(() => {
        setLoading(false);
        setIsInitialized(true);
        initGuardRef.current = false;
      }, 50);
    }
        
  }, [currentTemplate, defaultTemplate, displayTemplates, isInitialized]);

  // Note: Steps and prospect form loading are now handled in the consolidated initialization useEffect above

  // Simple validation: ensure selectedTemplate exists in displayTemplates
  useEffect(() => {
    // Only run this validation when displayTemplates changes
    // Use a callback to avoid stale closure issues
    setSelectedTemplate(prev => {
      if (prev && !displayTemplates[prev]) {
        // If the current selectedTemplate doesn't exist, fall back to example-script
        return 'example-script';
      }
      return prev;
    });
  }, [displayTemplates]); // Only depend on displayTemplates

  useEffect(() => {
    if (selectedNode) {
      setEditingResponseData(selectedNode.responses || {});
    }
  }, [selectedNode]);
    
  // Separate useEffect to set originalId when a node is selected for editing
  useEffect(() => {
    if (selectedNode && !selectedNode.originalId) {
      setSelectedNode(prev => {
        if (!prev || typeof prev !== 'object') {
          console.warn('⚠️ setSelectedNode: prev is invalid during originalId set:', prev);
          return prev;
        }
        const safe = createSafeSelectedNode(prev, prev.id);
        safe.originalId = prev.id;
        return safe;
      });
    }
  }, [selectedNode]);

  // Ensure editingResponseData stays in sync with localScriptGraph changes
  // But don't overwrite if we're currently editing (hasUnsavedChanges)
  useEffect(() => {
    if (selectedNode && localScriptGraph.nodes[selectedNode.id] && !hasUnsavedChanges) {
      const currentNode = localScriptGraph.nodes[selectedNode.id];
      if (currentNode.responses && JSON.stringify(currentNode.responses) !== JSON.stringify(editingResponseData)) {
        setEditingResponseData(currentNode.responses);
      }
    }
  }, [localScriptGraph, selectedNode, hasUnsavedChanges, editingResponseData]);

  // Notify parent component about unsaved changes
  useEffect(() => {
    if (onUnsavedChangesChange) {
      onUnsavedChangesChange(templateDirty || hasUnsavedChanges);
    }
  }, [templateDirty, hasUnsavedChanges, onUnsavedChangesChange]);

  // Notify parent about autoSave state
  useEffect(() => {
    if (onAutoSaveChange) {
      onAutoSaveChange(autoSave);
    }
  }, [autoSave, onAutoSaveChange]);

  // Register save function with parent so it can trigger save before navigation
  useEffect(() => {
    if (onRegisterSave) {
      onRegisterSave(() => handleSaveChangesDirectly());
    }
    return () => { if (onRegisterSave) onRegisterSave(null); };
  }, [onRegisterSave]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flowchart modal removed - navigation now handled via React Router

  // Reset dirty flag when opening a node for editing (selectedNode changes)
  const prevSelectedNodeRef = useRef(null);
  useEffect(() => {
    if (selectedNode && selectedNode !== prevSelectedNodeRef.current) {
      setHasUnsavedChanges(false);
    }
    prevSelectedNodeRef.current = selectedNode;
  }, [selectedNode]);

  // Close node edit panel on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && selectedNode && viewMode !== 'edit') {
        setSelectedNode(null);
        setEditingResponseData({});
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedNode, viewMode]);

  useEffect(() => {
    if (viewMode === 'responses' && selectedNode) {
      setEditingResponseData(selectedNode.responses || {});
    }
  }, [viewMode, selectedNode]);

  // Centralize marking unsaved changes
  const markDirty = useCallback(() => {
    setHasUnsavedChanges(true);
    setTemplateDirty(true);
  }, []);

  // PHASE 3 FIX: Handle autosave toggle to preserve temp changes
  const handleAutoSaveToggle = useCallback((newValue) => {
    console.log('🔄 AutoSave toggling:', autoSave, '→', newValue);
        
    setAutoSave(newValue);
        
    if (newValue) {
      setAutoSaveStatus('AutoSave enabled');
    } else {
      setAutoSaveStatus('AutoSave disabled - changes will be saved manually');
    }
  }, [autoSave]);

  const updateProspectForm = useCallback((newFormConfig) => {
    console.log('ScriptBuilder: Updating prospect form:', newFormConfig);
    setProspectForm(newFormConfig);
    markDirty();
    console.log('ScriptBuilder: Marked template as dirty after prospect form change');
  }, [markDirty]);

  const debounceTimeoutRef = useRef(null);
  useEffect(() => {
    // Autosave enabled for editable templates (not read-only)
    // Note: All templates are now team-level (no global templates loaded)
    const isAutoSaveEnabled = autoSave &&
            selectedTemplate &&
            displayTemplates[selectedTemplate] &&
            !displayTemplates[selectedTemplate].isReadOnly;

    // Debug autosave state
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Autosave useEffect triggered:', {
        autoSave,
        selectedTemplate,
        templateDirty,
        isAutoSaveEnabled,
        hasTemplate: !!displayTemplates[selectedTemplate],
        isReadOnly: displayTemplates[selectedTemplate]?.isReadOnly
      });
    }

    if (isAutoSaveEnabled && templateDirty && !initGuardRef.current) {
      console.log('🔄 ScriptBuilder: Autosave triggered, template dirty state:', templateDirty);
      setAutoSaveStatus('Saving...');
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          const templateData = {
            graph: localScriptGraph,
            steps: steps,
            prospectForm: prospectForm,
          };
          console.log('ScriptBuilder: Saving template data:', templateData);
          const templateName = displayTemplates[selectedTemplate].name;
          await onUpdateTemplate(selectedTemplate, templateData, templateName, true); // isAutoSave = true
          setTemplateDirty(false);
          setHasUnsavedChanges(false);
          // Update autosave status without causing visual refresh
          setAutoSaveStatus(`Last saved at ${new Date().toLocaleTimeString()}`);
        } catch (error) {
          console.error('❌ Auto-save error:', error);
          console.error('❌ Auto-save error details:', error.message, error.stack);
          setAutoSaveStatus('Error saving. Please save manually.');
        }
      }, 500); // 500ms debounce for faster autosave
    }
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [localScriptGraph, steps, prospectForm, autoSave, selectedTemplate, templateDirty, availableTemplates, onUpdateTemplate]);

  // 1-minute interval autosave (backup save)
  const intervalAutoSaveRef = useRef(null);
  useEffect(() => {
    // Autosave enabled for editable templates (not read-only)
    // Note: All templates are now team-level (no global templates loaded)
    const isAutoSaveEnabled = autoSave &&
            selectedTemplate &&
            displayTemplates[selectedTemplate] &&
            !displayTemplates[selectedTemplate].isReadOnly;

    if (isAutoSaveEnabled) {
      // Clear existing interval
      if (intervalAutoSaveRef.current) {
        clearInterval(intervalAutoSaveRef.current);
      }
            
      intervalAutoSaveRef.current = setInterval(async () => {
        if (templateDirty && !initGuardRef.current) {
          try {
            const templateData = {
              graph: localScriptGraph,
              steps: steps,
              prospectForm: prospectForm,
            };
            const templateName = displayTemplates[selectedTemplate].name;
            await onUpdateTemplate(selectedTemplate, templateData, templateName);
            setTemplateDirty(false);
            setHasUnsavedChanges(false);
            setAutoSaveStatus(`Auto-saved at ${new Date().toLocaleTimeString()}`);
          } catch (error) {
            console.error('Interval auto-save error:', error);
          }
        }
      }, 60000); // 60 seconds
    } else {
      // Clear interval if autosave is disabled
      if (intervalAutoSaveRef.current) {
        clearInterval(intervalAutoSaveRef.current);
      }
    }
        
    return () => {
      if (intervalAutoSaveRef.current) {
        clearInterval(intervalAutoSaveRef.current);
      }
    };
  }, [autoSave, selectedTemplate, availableTemplates, onUpdateTemplate, localScriptGraph, steps, prospectForm, templateDirty]);

  // Handle ID rename without exiting edit mode
  const handleNodeIdRename = useCallback(async () => {
    if (!selectedNode) return;
        
    const oldNodeId = selectedNode.originalId || selectedNode.id;
    const newNodeId = selectedNode.id;
        
    if (oldNodeId === newNodeId) {
      return; // No change
    }

    const confirmed = await confirmDialog(
      `⚠️ Renaming this node will affect your analytics\n\n` +
      `The node ID "${oldNodeId}" is used to track agent performance in Command Center. ` +
      `Renaming it will break the connection to historical data for this node, affecting:\n\n` +
      `• Drop-off Analysis — historical drop-off counts for "${oldNodeId}" will disappear\n` +
      `• Script Health — completion rates tied to this node may shift\n` +
      `• AI Improve — improvement suggestions based on this node's history will be lost\n\n` +
      `To change what the agent says, edit the script content instead — that never affects analytics.`,
      { variant: 'danger', confirmText: 'Rename anyway' }
    );
    if (!confirmed) return;
        
    // Create new node with new ID, move position, and update all inbound references
    setLocalScriptGraph(prev => {
      const newNodes = { ...prev.nodes };
      // Get existing node data or use selectedNode
      const existingNode = newNodes[oldNodeId] || {};
            
      // Remove old entry and add updated node with new ID
      delete newNodes[oldNodeId];
      newNodes[newNodeId] = {
        ...existingNode,
        ...selectedNode,
        id: newNodeId,
        script: selectedNode.script,
        coaching: selectedNode.coaching,
        responses: editingResponseData,
        aiConfig: selectedNode.aiConfig
      };

      // Update responses across all nodes that pointed to the old ID
      Object.entries(newNodes).forEach(([nid, node]) => {
        if (!node?.responses) return;
        const updatedResponses = { ...node.responses };
        let hasChange = false;
        Object.entries(updatedResponses).forEach(([rk, resp]) => {
          if (resp?.nextNode === oldNodeId) {
            updatedResponses[rk] = { ...resp, nextNode: newNodeId };
            hasChange = true;
          }
        });
        if (hasChange) {
          newNodes[nid] = { ...node, responses: updatedResponses };
        }
      });

      // Move UI position mapping to new ID
      const newPositions = { ...(prev.ui?.positions || {}) };
      if (newPositions[oldNodeId]) {
        newPositions[newNodeId] = newPositions[oldNodeId];
        delete newPositions[oldNodeId];
      }

      // Record old→new alias so analytics from before this rename continue to resolve
      const existingAliases = prev.nodeAliases || {};
      // If oldNodeId was itself an alias target, chain it: any old key pointing to oldNodeId now points to newNodeId
      const updatedAliases = Object.fromEntries(
        Object.entries(existingAliases).map(([k, v]) => [k, v === oldNodeId ? newNodeId : v])
      );
      updatedAliases[oldNodeId] = newNodeId;

      return {
        ...prev,
        nodes: newNodes,
        nodeAliases: updatedAliases,
        ui: {
          ...prev.ui,
          positions: newPositions
        }
      };
    });
        
    // Update selectedNode's originalId to the new ID so subsequent renames work correctly
    setSelectedNode(prev => ({
      ...prev,
      originalId: newNodeId
    }));
        
    setTemplateDirty(true);
    console.log('✅ Node renamed:', oldNodeId, '→', newNodeId, '(alias recorded)');
  }, [selectedNode, editingResponseData]);

  const handleSaveNode = () => {
    if (!selectedNode) return;
        
    // Special handling for end script nodes
    if (selectedNode.isEndScript) {
      setLocalScriptGraph(prev => ({
        ...prev,
        endScriptConfig: {
          ...prev.endScriptConfig,
          script: selectedNode.script,
          coaching: selectedNode.coaching
        }
      }));
            
      setSelectedNode(null);
      setHasUnsavedChanges(false);
      setTemplateDirty(true);
      showSuccess('End script saved successfully!');
      return;
    }
        
    // If the node ID has changed, we need to update the node structure
    const oldNodeId = selectedNode.originalId || selectedNode.id;
    const newNodeId = selectedNode.id;
        
    if (oldNodeId !== newNodeId) {
      // Create new node with new ID, move position, and update all inbound references
      setLocalScriptGraph(prev => {
        const newNodes = { ...prev.nodes };
        // Remove old entry and add updated node with new ID
        delete newNodes[oldNodeId];
        newNodes[newNodeId] = {
          ...selectedNode,
          id: newNodeId,
          script: selectedNode.script,
          coaching: selectedNode.coaching,
          responses: editingResponseData,
          aiConfig: selectedNode.aiConfig
        };

        // Update responses across all nodes that pointed to the old ID
        Object.entries(newNodes).forEach(([nid, node]) => {
          if (!node?.responses) return;
          const updatedResponses = { ...node.responses };
          let hasChange = false;
          Object.entries(updatedResponses).forEach(([rk, resp]) => {
            if (resp?.nextNode === oldNodeId) {
              updatedResponses[rk] = { ...resp, nextNode: newNodeId };
              hasChange = true;
            }
          });
          if (hasChange) {
            newNodes[nid] = { ...node, responses: updatedResponses };
          }
        });

        // Move UI position mapping to new ID
        const newPositions = { ...(prev.ui?.positions || {}) };
        if (newPositions[oldNodeId]) {
          newPositions[newNodeId] = newPositions[oldNodeId];
          delete newPositions[oldNodeId];
        }

        // Record old→new alias so analytics from before this rename continue to resolve
        const existingAliases = prev.nodeAliases || {};
        const updatedAliases = Object.fromEntries(
          Object.entries(existingAliases).map(([k, v]) => [k, v === oldNodeId ? newNodeId : v])
        );
        updatedAliases[oldNodeId] = newNodeId;

        return {
          ...prev,
          nodes: newNodes,
          nodeAliases: updatedAliases,
          ui: {
            ...prev.ui,
            positions: newPositions
          }
        };
      });
    } else {
      // Just update the existing node
      setLocalScriptGraph(prev => ({
        ...prev,
        nodes: {
          ...prev.nodes,
          [selectedNode.id]: {
            ...selectedNode,
            script: selectedNode.script,
            coaching: selectedNode.coaching,
            responses: editingResponseData,
            aiConfig: selectedNode.aiConfig
          }
        }
      }));
    }
        
    setSelectedNode(null);
    setEditingResponseData({});
    setHasUnsavedChanges(false);
    setTemplateDirty(true);
    showSuccess('Node saved successfully!');
  };

  const handleRemoveResponse = (responseKey) => {
    const newResponses = { ...editingResponseData };
    delete newResponses[responseKey];
    setEditingResponseData(newResponses);
        
    // Auto-sync to localScriptGraph when auto-save is enabled
    if (selectedNode && autoSave) {
      setLocalScriptGraph(prev => ({
        ...prev,
        nodes: {
          ...prev.nodes,
          [selectedNode.id]: {
            ...prev.nodes[selectedNode.id],
            responses: newResponses
          }
        }
      }));
    }
        
    markDirty();
  };

  const handleUpdateResponse = (responseKey, field, value) => {
    console.log('🔄 handleUpdateResponse called:', { responseKey, field, value });
    console.log('📝 Current editingResponseData before update:', Object.keys(editingResponseData));
        
    setEditingResponseData(prev => {
      const updatedResponse = {
        ...prev[responseKey],
        [field]: value
      };
            
      // If changing to open_text type and no data field exists, auto-generate one
      if (field === 'type' && value === 'open_text' && !updatedResponse.dataField) {
        if (updatedResponse.label) {
          updatedResponse.dataField = generateDataFieldName(updatedResponse.label);
        } else {
          updatedResponse.dataField = 'user_input';
        }
      }
            
      // If updating label and it's an open_text response, regenerate data field name
      if (field === 'label' && updatedResponse.type === 'open_text') {
        updatedResponse.dataField = generateDataFieldName(value);
      }
            
      console.log('📝 Updated response:', updatedResponse);
            
      const newResponseData = {
        ...prev,
        [responseKey]: updatedResponse
      };
            
      // Auto-sync to localScriptGraph when auto-save is enabled
      if (selectedNode && autoSave) {
        setLocalScriptGraph(prevGraph => ({
          ...prevGraph,
          nodes: {
            ...prevGraph.nodes,
            [selectedNode.id]: {
              ...prevGraph.nodes[selectedNode.id],
              responses: newResponseData
            }
          }
        }));
      }
            
      return newResponseData;
    });
    markDirty();
  };

  const handleAddResponse = () => {
    const newKey = getNextResponseKey(editingResponseData);
    setEditingResponseData(prev => {
      // Find existing open text responses to inherit next node
      const existingOpenTextResponses = Object.values(prev).filter(response => response.type === 'open_text');
      const inheritedNextNode = existingOpenTextResponses.length > 0 ? existingOpenTextResponses[0].nextNode : '';
            
      const newResponseData = {
        ...prev,
        [newKey]: {
          type: 'predetermined', // Set default type
          nextNode: inheritedNextNode, // Inherit from existing open text responses
          data: {},
          label: '',
          placeholder: '', // Optional - only shown when checkbox is checked
          dataField: ''
        }
      };
            
      // Auto-sync to localScriptGraph when auto-save is enabled
      if (selectedNode && autoSave) {
        setLocalScriptGraph(prevGraph => ({
          ...prevGraph,
          nodes: {
            ...prevGraph.nodes,
            [selectedNode.id]: {
              ...prevGraph.nodes[selectedNode.id],
              responses: newResponseData
            }
          }
        }));
      }
            
      return newResponseData;
    });
    markDirty();
  };

  const handleDeleteNode = (nodeId) => {
    setNodeToDelete(nodeId);
    setShowDeleteNodeConfirm(true);
  };

  const confirmDeleteNode = () => {
    if (!nodeToDelete) return;
        
    const newNodes = { ...localScriptGraph.nodes };
    delete newNodes[nodeToDelete];
        
    // Remove references to this node from other nodes' responses
    Object.keys(newNodes).forEach(key => {
      if (newNodes[key].responses) {
        Object.keys(newNodes[key].responses).forEach(responseKey => {
          if (newNodes[key].responses[responseKey].nextNode === nodeToDelete) {
            newNodes[key].responses[responseKey].nextNode = '';
          }
        });
      }
    });
        
    // Also remove the node's position from ui.positions
    const newPositions = { ...localScriptGraph.ui?.positions };
    delete newPositions[nodeToDelete];
        
    setLocalScriptGraph(prev => ({
      ...prev,
      nodes: newNodes,
      ui: {
        ...prev.ui,
        positions: newPositions
      }
    }));
    setTemplateDirty(true);
        
    setShowDeleteNodeConfirm(false);
    setNodeToDelete(null);
  };

  // Lightweight close confirm respecting unsaved edits
  const handleCloseWithConfirm = async () => {
    const activeName = displayTemplates[selectedTemplate]?.name || 'Template';
    if (!autoSave && ((selectedNode && hasUnsavedChanges) || templateDirty)) {
      const ok = await confirmDialog(
        `You have unsaved changes to your template: ${activeName}. Close without saving?`,
        { variant: 'danger', confirmText: 'Close Without Saving' }
      );
      if (!ok) return;
    }
    onClose();
  };

  const handleAddNode = () => {
    // Use the enhanced node creator instead of the old 'add' view mode
    setCurrentResponseKey(null); // No specific response key when adding from bottom
    setNodeCreationData({
      id: '',
      script: '',
      coaching: '',
      step: steps[0]?.id || defaultSteps[0]?.id || 'interest'
    });
    setShowEnhancedNodeCreator(true);
  };

  const handleSaveNewNode = () => {
    if (!newNodeData.id) return;
        
    // Validate the node name
    const validationError = validateNodeName(newNodeData.id);
    if (validationError) {
      showError(validationError);
      return;
    }
        
    const nodeToSave = {
      ...newNodeData,
      id: newNodeData.id,
      script: newNodeData.script,
      coaching: newNodeData.coaching,
      responses: newNodeData.responses || {},
      requiresTextInput: newNodeData.requiresTextInput || false,
      createdAt: Date.now(), // Add timestamp for sorting
      aiConfig: newNodeData.aiConfig || {
        canGenerateCustomScript: true,
        canAddCustomResponses: true,
        canSkipToAnyNode: false,
        contextWindow: 5,
        fallbackNode: 'objection_not_interested'
      }
    };
        
    setLocalScriptGraph(prev => ({
      ...prev,
      nodes: {
        ...prev.nodes,
        [newNodeData.id]: nodeToSave
      }
    }));
        
    setViewMode('flow-connected');
    setNewNodeData({});
    setTemplateDirty(true);
  };

  const validateTemplateName = (name, excludeId = null) => {
    if (!name.trim()) {
      return 'Template name cannot be empty';
    }
        
    // Check for duplicate names (excluding the current template being edited)
    const existingNames = Object.entries(availableTemplates)
      .filter(([id, template]) => id !== excludeId) // Exclude current template when editing
      .map(([id, template]) => template.name.toLowerCase());
        
    if (existingNames.includes(name.toLowerCase())) {
      return 'A template with this name already exists';
    }
        
    return '';
  };

  const handleApplyChanges = async () => {
    setIsSaving(true);
    setTemplateNameError('');
        
    // Validate template name
    const nameError = validateTemplateName(templateName, editingTemplateId);
    if (nameError) {
      setTemplateNameError(nameError);
      setIsSaving(false);
      return;
    }
        
    try {
      // Create a template object that includes both the script graph and steps configuration
      const templateData = {
        graph: localScriptGraph,
        steps: steps,
        prospectForm: prospectForm
      };
            
      if (editingTemplateId) {
        await onUpdateTemplate(editingTemplateId, templateData, templateName);
      } else {
        await onApplyChanges(templateData, templateName);
      }
      setTemplateDirty(false);
      // Don't close ScriptBuilder after saving - stay in it to continue working
            
      // Show success message
      const action = editingTemplateId ? 'updated' : 'saved';
      showSuccess(`Template ${action} successfully!`);
            
      // Close the template dialog
      setShowTemplateDialog(false);
      setEditingTemplateId(null);
            
      // If this was a new template creation, reset the template name to prepare for next creation
      if (!editingTemplateId) {
        // Don't reset the template name immediately - let the useEffect handle it
        // when the currentTemplate prop updates
      }
    } catch (error) {
      console.error('Error applying changes:', error);
      showError('Failed to save template. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateBlankTemplate = async () => {
    setIsSaving(true);
    setTemplateNameError('');
        
    // Validate template name
    const nameError = validateTemplateName(templateName, null);
    if (nameError) {
      setTemplateNameError(nameError);
      setIsSaving(false);
      return;
    }
        
    try {
      console.log('🆕 Creating blank template with name:', templateName);
            
      // Create a minimal template with only opening and end nodes
      const blankGraph = {
        nodes: {
          'opening': {
            id: 'opening',
            script: 'Hi, I\'m calling about your property. Have you ever considered selling your home?',
            coaching: 'Start with a simple, direct question about selling interest.',
            responses: {
              'yes': { 
                nextNode: '__end_script__',
                data: { openToSelling: true },
                label: 'Yes, I\'m interested'
              },
              'no': { 
                nextNode: '__end_script__',
                data: { openToSelling: false },
                label: 'No, not interested'
              }
            },
            requiresTextInput: false
          }
        },
        endScriptConfig: {
          script: 'Thank you for your time. I\'ll now collect some basic information about your property to better assist you.',
          coaching: 'Keep it brief and professional. Transition smoothly to data collection.'
        },
        ui: {
          positions: {
            'opening': { x: 100, y: 100 }
            // Note: END_SCRIPT position will be set by FlowChartEditor automatically
          }
        }
      };
            
      const blankSteps = [
        { id: 'interest', name: 'Interest', description: 'Ensuring there is interest to sell' },
        { id: 'closing', name: 'Closing', description: 'Wrapping up the conversation' }
      ];
            
      const templateData = {
        graph: blankGraph,
        steps: blankSteps,
        name: templateName,
        createdAt: new Date(),
        creatorId: user?.email,
        canEdit: true,
        isReadOnly: false
      };
            
      console.log('🆕 Template data structure:', JSON.stringify(templateData, null, 2));
      console.log('🆕 Calling onApplyChanges with:', { templateData, templateName });
      await onApplyChanges(templateData, templateName);
      console.log('🆕 onApplyChanges completed successfully');
      setTemplateDirty(false);
            
      // Show success message
      showSuccess('Blank template created successfully!');
            
      // Close dialog and clear name
      setShowTemplateDialog(false);
      setTemplateName('');
      setTemplateNameError('');
    } catch (error) {
      console.error('🚨 Error creating blank template:', error);
      console.error('🚨 Error details:', error.message, error.stack);
      setTemplateNameError(`Failed to create blank template: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const doLoadTemplate = (templateId) => {
    initGuardRef.current = true;
    logScriptBuilderAction('Template loading initiated', {
      templateId: templateId,
      previousTemplate: selectedTemplate,
      availableTemplatesCount: availableTemplates?.length || 0
    });

    setSelectedTemplate(templateId);
        
    // Notify parent component about template selection change
    if (onTemplateSelectionChange) {
      onTemplateSelectionChange(templateId);
    }
        
    if (templateId === 'example-script') {
      logScriptBuilderAction('Loading example script template', {
        templateId: templateId
      });
      setLocalScriptGraph(scriptGraph);
      setSteps([
        { id: 'interest', name: 'Interest', description: 'Ensuring there is interest to sell' },
        { id: 'motivation', name: 'Motivation', description: 'Determining motivations to sell' },
        { id: 'condition', name: 'Condition', description: 'Learning about house condition and call to action' }
      ]);
      // Reset to default prospect form for example script
      setProspectForm({
        title: 'Lead Data',
        fields: [
          { id: 'agentName', label: 'Agent Name', type: 'text', placeholder: 'Enter your name', required: true },
          { id: 'prospectName', label: 'Prospect Name', type: 'text', placeholder: 'Enter prospect\'s full name', required: true },
          { id: 'company', label: 'Company', type: 'text', placeholder: 'Enter company name', required: false },
          { id: 'prospectPhone', label: 'Phone Number', type: 'tel', placeholder: 'Enter phone number', required: true }
        ]
      });
      setTemplateName(`Custom Script ${new Date().toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: '2-digit' 
      }).replace(/\//g, '/')}, ${new Date().toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      })}`);
    } else {
      const template = displayTemplates[templateId];
      if (template) {
        logScriptBuilderAction('Loading custom template', {
          templateId: templateId,
          templateName: template.name,
          hasCustomSteps: !!template.steps,
          hasCustomProspectForm: !!template.prospectForm,
          nodeCount: (template?.data?.scriptGraph?.nodes || template?.graph?.nodes) ? Object.keys(template?.data?.scriptGraph?.nodes || template?.graph?.nodes).length : 0
        });

        // Handle both new structure (data.scriptGraph) and legacy structure (graph)
        const templateGraph = template?.data?.scriptGraph || template?.graph || { nodes: {}, ui: { positions: {} } };
        setLocalScriptGraph(templateGraph);
        // Load steps configuration if it exists, otherwise use defaults
        if (template.steps) {
          setSteps(template.steps);
        } else {
          setSteps(defaultSteps);
        }
        // Load prospect form configuration if it exists, otherwise use defaults
        if (template.prospectForm) {
          setProspectForm(template.prospectForm);
        } else {
          setProspectForm({
            title: 'Lead Data',
            fields: [
              { id: 'agentName', label: 'Agent Name', type: 'text', placeholder: 'Enter your name', required: true },
              { id: 'prospectName', label: 'Prospect Name', type: 'text', placeholder: 'Enter prospect\'s full name', required: true },
              { id: 'company', label: 'Company', type: 'text', placeholder: 'Enter company name', required: false },
              { id: 'prospectPhone', label: 'Phone Number', type: 'tel', placeholder: 'Enter phone number', required: true }
            ]
          });
        }
        // Update template name to match the loaded template
        if (template.name) {
          setTemplateName(template.name);
        }

        logScriptBuilderAction('Template loaded successfully', {
          templateId: templateId,
          templateName: template.name,
          stepsCount: template.steps?.length || 3,
          fieldsCount: template.prospectForm?.fields?.length || 4
        });
      } else {
        logScriptBuilderAction('Template load failed - template not found', {
          templateId: templateId,
          availableTemplatesCount: Object.keys(availableTemplates).length
        });
      }
    }

    logScriptBuilderAction('Template loading completed', {
      templateId: templateId,
      selectedTemplate: templateId
    });
  };

  const handleLoadTemplate = (templateId) => {
    if (templateId === selectedTemplate) return;
    if (!autoSave && templateDirty) {
      toast(
        (t) => (
          <div>
            <p style={{ marginBottom: '12px', fontWeight: 500 }}>You have unsaved changes.</p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => toast.dismiss(t.id)}
                style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #D1D5DB', background: '#fff', color: '#374151', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
              >
                Cancel
              </button>
              <button
                onClick={() => { toast.dismiss(t.id); if (refreshTemplates) refreshTemplates(); doLoadTemplate(templateId); }}
                style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #D1D5DB', background: '#fff', color: '#DC2626', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
              >
                Discard
              </button>
              <button
                onClick={async () => {
                  toast.dismiss(t.id);
                  try { await handleSaveChangesDirectly(); } catch (e) { console.error('Save before template switch failed:', e); }
                  doLoadTemplate(templateId);
                }}
                style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: '#2563EB', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
              >
                Save
              </button>
            </div>
          </div>
        ),
        {
          duration: Infinity,
          position: 'top-center',
          style: {
            background: '#fff', color: '#374151', border: '1px solid #E5E7EB',
            borderRadius: '8px', padding: '20px', fontSize: '14px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
            maxWidth: '420px', minWidth: '320px'
          }
        }
      );
      return;
    }
    doLoadTemplate(templateId);
  };

  const handleEditTemplate = (templateId) => {
    setEditingTemplateId(templateId);
    setTemplateName(displayTemplates[templateId]?.name || 'Custom Script');
    setTemplateNameError(''); // Clear any previous errors
    setShowTemplateDialog(true);
  };

  const handleSaveChangesDirectly = async () => {
    if (!selectedTemplate || displayTemplates[selectedTemplate]?.isReadOnly) {
      return;
    }

    setIsSaving(true);
    try {
      const templateData = {
        name: displayTemplates[selectedTemplate]?.name,
        graph: localScriptGraph,
        steps: steps,
        prospectForm: prospectForm,
        metadata: {
          lastModified: new Date().toISOString(),
          version: (displayTemplates[selectedTemplate]?.metadata?.version || 0) + 1
        }
      };

      const templateName = displayTemplates[selectedTemplate]?.name;
      await onUpdateTemplate(selectedTemplate, templateData, templateName);
      setTemplateDirty(false);
      setHasUnsavedChanges(false);
            
      // Refresh DataContext so FlowchartEditor and other components see latest data
      if (refreshTemplates) {
        refreshTemplates();
      }
            
      // Show success message
      showSuccess('Template updated successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      showError(`Error saving template: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateStep = (index, field, value) => {
    const newSteps = steps.map((step, i) => 
      i === index ? { ...step, [field]: value } : step
    );
    setSteps(newSteps);
        
    // Auto-sync to localScriptGraph when auto-save is enabled
    if (autoSave) {
      setLocalScriptGraph(prev => ({
        ...prev,
        steps: newSteps
      }));
    }
        
    setTemplateDirty(true);
  };

  const handleAddStep = () => {
    const newSteps = [...steps, {
      id: `step_${steps.length + 1}`,
      name: 'New Step',
      description: 'Step description'
    }];
    setSteps(newSteps);
        
    // Auto-sync to localScriptGraph when auto-save is enabled
    if (autoSave) {
      setLocalScriptGraph(prev => ({
        ...prev,
        steps: newSteps
      }));
    }
        
    setTemplateDirty(true);
  };

  const handleRemoveStep = (index) => {
    const stepToRemove = steps[index];
    const stepId = stepToRemove.id;
        
    // Safety check: Ensure no nodes are associated with this step
    const nodesInStep = groupedNodes[stepId] || [];
    if (nodesInStep.length > 0) {
      console.warn('🚫 Attempted to delete phase with nodes:', {
        stepId,
        nodeCount: nodesInStep.length,
        nodeIds: nodesInStep
      });
      showWarning(`Cannot delete phase "${stepToRemove.name}" - it contains ${nodesInStep.length} node(s). Please move or delete all nodes first.`);
      return;
    }
        
    console.log('✅ Deleting empty phase:', {
      stepId,
      stepName: stepToRemove.name,
      index
    });
        
    const newSteps = steps.filter((_, i) => i !== index);
    setSteps(newSteps);
        
    // Auto-sync to localScriptGraph when auto-save is enabled
    if (autoSave) {
      setLocalScriptGraph(prev => ({
        ...prev,
        steps: newSteps
      }));
    }
        
    setTemplateDirty(true);
  };

  // Objection response handlers
  const handleAddObjection = (stepId) => {
    setShowAddObjection(stepId);
    setNewObjectionData({ label: '', response: '' });
  };

  const handleSaveObjection = (stepId) => {
    if (!newObjectionData.label.trim() || !newObjectionData.response.trim()) {
      return;
    }

    const objectionId = `obj_${Date.now()}`;
    const newObjection = {
      id: objectionId,
      label: newObjectionData.label.trim(),
      response: newObjectionData.response.trim()
    };

    // Update steps with new objection
    const newSteps = steps.map(step => {
      if (step.id === stepId) {
        const objectionResponses = step.objectionResponses || [];
        return {
          ...step,
          objectionResponses: [...objectionResponses, newObjection]
        };
      }
      return step;
    });
    setSteps(newSteps);

    // Create objection handling node in the script graph
    const objectionNodeId = `objection_${objectionId}`;
    setLocalScriptGraph(prev => {
      // Check if the objection node already exists to avoid duplicates
      if (prev.nodes[objectionNodeId]) {
        console.log('🔄 Objection node already exists:', objectionNodeId);
        return prev;
      }
            
      return {
        ...prev,
        nodes: {
          ...prev.nodes,
          [objectionNodeId]: {
            id: objectionNodeId,
            script: newObjection.response,
            coaching: `Handle objection: ${newObjection.label}`,
            responses: {
              'continue': {
                nextNode: '__return_to_previous__', // Special marker to return to previous question
                data: {},
                label: 'Continue with question'
              },
              'end_call': {
                nextNode: '__end_script__',
                data: {},
                label: 'End Call'
              }
            },
            step: stepId,
            isObjectionNode: true,
            requiresTextInput: false,
            aiConfig: {
              canGenerateCustomScript: true,
              canAddCustomResponses: true,
              canSkipToAnyNode: false,
              contextWindow: 5,
              fallbackNode: '__end_script__'
            }
          }
        }
      };
    });

    setShowAddObjection(null);
    setNewObjectionData({ label: '', response: '' });
    setTemplateDirty(true);
  };

  const handleEditObjection = (stepId, objectionIndex, field, value) => {
    const newSteps = steps.map(step => {
      if (step.id === stepId) {
        const objectionResponses = [...(step.objectionResponses || [])];
        objectionResponses[objectionIndex] = {
          ...objectionResponses[objectionIndex],
          [field]: value
        };
        return {
          ...step,
          objectionResponses
        };
      }
      return step;
    });
    setSteps(newSteps);

    // Update the corresponding objection node in script graph
    const objection = steps.find(s => s.id === stepId)?.objectionResponses?.[objectionIndex];
    if (objection) {
      const objectionNodeId = `objection_${objection.id}`;
      setLocalScriptGraph(prev => {
        const existingNode = prev.nodes[objectionNodeId];
                
        // If objection node doesn't exist, create it with proper structure
        if (!existingNode) {
          console.warn('⚠️ Objection node not found, creating it:', objectionNodeId);
          return {
            ...prev,
            nodes: {
              ...prev.nodes,
              [objectionNodeId]: {
                id: objectionNodeId,
                script: field === 'response' ? value : objection.response || '',
                coaching: field === 'label' ? `Handle objection: ${value}` : `Handle objection: ${objection.label || ''}`,
                responses: {
                  'continue': {
                    nextNode: '__return_to_previous__',
                    data: {},
                    label: 'Continue with question'
                  },
                  'end_call': {
                    nextNode: '__end_script__',
                    data: {},
                    label: 'End Call'
                  }
                },
                step: stepId,
                isObjectionNode: true,
                requiresTextInput: false,
                aiConfig: {
                  canGenerateCustomScript: true,
                  canAddCustomResponses: true,
                  canSkipToAnyNode: false,
                  contextWindow: 5,
                  fallbackNode: '__end_script__'
                }
              }
            }
          };
        }
                
        // Update existing node
        return {
          ...prev,
          nodes: {
            ...prev.nodes,
            [objectionNodeId]: {
              ...existingNode,
              script: field === 'response' ? value : existingNode.script || '',
              coaching: field === 'label' ? `Handle objection: ${value}` : existingNode.coaching || ''
            }
          }
        };
      });
    }

    setTemplateDirty(true);
  };

  const handleRemoveObjection = (stepId, objectionIndex) => {
    const step = steps.find(s => s.id === stepId);
    const objection = step?.objectionResponses?.[objectionIndex];
        
    if (objection) {
      // Remove objection node from script graph
      const objectionNodeId = `objection_${objection.id}`;
      setLocalScriptGraph(prev => {
        const newNodes = { ...prev.nodes };
        delete newNodes[objectionNodeId];
        return {
          ...prev,
          nodes: newNodes
        };
      });
    }

    // Remove objection from step
    const newSteps = steps.map(step => {
      if (step.id === stepId) {
        const objectionResponses = [...(step.objectionResponses || [])];
        objectionResponses.splice(objectionIndex, 1);
        return {
          ...step,
          objectionResponses
        };
      }
      return step;
    });
    setSteps(newSteps);
    setTemplateDirty(true);
  };

  // Bulk node operations removed — grid-based selection/move replaced by accordion-based phase management

  // Helper function to safely create selectedNode object without circular references
  const createSafeSelectedNode = (node, nodeId) => {
    // Handle case where node might be undefined or incomplete
    if (!node || typeof node !== 'object') {
      console.warn('⚠️ createSafeSelectedNode called with invalid node:', { node, nodeId });
      return {
        id: nodeId || 'unknown',
        script: '',
        coaching: '',
        responses: {},
        step: '',
        isOpeningNode: false,
        aiConfig: {},
        requiresTextInput: false,
        originalId: nodeId || 'unknown'
      };
    }
        
    return {
      id: nodeId || node.id || 'unknown',
      script: node.script || '',
      coaching: node.coaching || '',
      responses: node.responses ? { ...node.responses } : {},
      step: node.step || '',
      isOpeningNode: node.isOpeningNode || false,
      aiConfig: node.aiConfig ? { ...node.aiConfig } : {},
      requiresTextInput: node.requiresTextInput || false,
      originalId: nodeId || node.id || 'unknown'
    };
  };

  // Auto-select node when initialNodeId is provided (from flowchart "Configure Details" button)
  useEffect(() => {
    if (!isInitialized || !initialNodeId || !localScriptGraph?.nodes) return;
    
    const node = localScriptGraph.nodes[initialNodeId];
    if (node) {
      console.log('🎯 Auto-selecting node from initialNodeId:', initialNodeId);
      const safeNode = createSafeSelectedNode(node, initialNodeId);
      safeNode.originalId = initialNodeId;
      setSelectedNode(safeNode);
      
      // Scroll to the node element after a brief delay for DOM update
      setTimeout(() => {
        const nodeElement = document.getElementById(`node-${initialNodeId}`);
        if (nodeElement) {
          nodeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add a brief highlight effect
          nodeElement.style.boxShadow = '0 0 0 3px #3b82f6';
          setTimeout(() => {
            nodeElement.style.boxShadow = '';
          }, 2000);
        }
      }, 100);
    } else {
      console.warn('⚠️ initialNodeId provided but node not found:', initialNodeId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, initialNodeId]);

  // Node sorting function - sorts by creation time (earliest first), falls back to alphabetical
  const sortNodeIds = (nodeIds) => {
    return [...nodeIds].sort((a, b) => {
      const nodeA = localScriptGraph.nodes[a];
      const nodeB = localScriptGraph.nodes[b];
      const timeA = nodeA?.createdAt;
      const timeB = nodeB?.createdAt;
            
      // If both nodes have timestamps, sort by creation time
      if (timeA && timeB) {
        return timeA - timeB; // Earliest first
      }
            
      // If neither node has timestamps, sort with natural numeric ordering
      if (!timeA && !timeB) {
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
      }
            
      // If only one has a timestamp, prioritize timestamped nodes first
      if (timeA && !timeB) {
        return -1; // Node A (with timestamp) comes first
      } else {
        return 1; // Node B (with timestamp) comes first
      }
    });
  };

  const toggleEditingObjections = (stepId) => {
    setEditingObjections(prev => ({
      ...prev,
      [stepId]: !prev[stepId]
    }));
  };


  // Node drag handlers (handleDragStart, handleDragOver, handleDrop, handleDragEnd) removed — grid view replaced

  // Response drag and drop handlers
  const handleResponseDragStart = (e, responseKey) => {
    setDraggedResponse(responseKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleResponseDragOver = (e) => {
    e.preventDefault();
  };

  const handleResponseDrop = (e, targetResponseKey) => {
    e.preventDefault();
    if (draggedResponse && targetResponseKey && draggedResponse !== targetResponseKey) {
      // Reorder responses
      const responses = { ...editingResponseData };
      const responseKeys = Object.keys(responses);
      const draggedIndex = responseKeys.indexOf(draggedResponse);
      const targetIndex = responseKeys.indexOf(targetResponseKey);
            
      // Remove dragged item
      const [draggedItem] = responseKeys.splice(draggedIndex, 1);
            
      // Insert at target position
      responseKeys.splice(targetIndex, 0, draggedItem);
            
      // Create new responses object with new order
      const newResponses = {};
      responseKeys.forEach(key => {
        newResponses[key] = responses[key];
      });
            
      setEditingResponseData(newResponses);
            
      // Auto-sync to localScriptGraph when auto-save is enabled
      if (selectedNode && autoSave) {
        setLocalScriptGraph(prev => ({
          ...prev,
          nodes: {
            ...prev.nodes,
            [selectedNode.id]: {
              ...prev.nodes[selectedNode.id],
              responses: newResponses
            }
          }
        }));
      }
    }
    setDraggedResponse(null);
    markDirty();
  };

  const handleResponseDragEnd = () => {
    setDraggedResponse(null);
  };

  // Phase drag and drop handlers
  const handlePhaseDragStart = (e, phaseIndex) => {
    console.log('🔥 Phase drag start:', phaseIndex);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', phaseIndex.toString()); // Required for some browsers
    setDraggedPhase(phaseIndex);
  };

  const handlePhaseDragOver = (e, phaseIndex) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
        
    console.log('🔄 Drag over:', { draggedPhase, phaseIndex, target: e.target.tagName });
        
    // Always allow drop and set drag over state
    if (draggedPhase !== null && draggedPhase !== phaseIndex) {
      setDragOverPhase(phaseIndex);
      console.log('✅ Set drag over phase:', phaseIndex);
    }
  };

  const handlePhaseDrop = (e, targetPhaseIndex) => {
    e.preventDefault();
    e.stopPropagation();
        
    console.log('🎯 Phase drop EVENT TRIGGERED:', { 
      draggedPhase, 
      targetPhaseIndex, 
      target: e.target.tagName,
      currentTarget: e.currentTarget.tagName 
    });
        
    if (draggedPhase === null || draggedPhase === targetPhaseIndex) {
      setDragOverPhase(null);
      return;
    }

    const newSteps = [...steps];
    const draggedStep = newSteps[draggedPhase];
        
    console.log('📋 Reordering steps:', { 
      originalOrder: newSteps.map(s => s.name),
      draggedStep: draggedStep.name,
      fromIndex: draggedPhase,
      toIndex: targetPhaseIndex
    });
        
    // Clean approach: move item from source to target position
    // Remove the item from its current position
    newSteps.splice(draggedPhase, 1);
        
    // Calculate where to insert it
    // When the user drags to a position, they want it to end up at that visual position
    // But we need to account for the fact that we removed an item first
    let insertPosition = targetPhaseIndex;
        
    // The issue: if we're moving down and subtract 1, we end up one position too high
    // Let's try NOT subtracting when moving down
    // This means the item will be inserted at the exact target position
        
    // Actually, let's think about it differently:
    // If user drags from position 0 to position 2, they want it at position 2
    // After removing item at 0, positions 1,2,3 become 0,1,2
    // So original position 2 is now at index 1
    // But if we insert at index 2, it will be at the end
    // If we insert at index 1, it will be where position 2 was
        
    // Let me try the simplest approach: don't adjust for moving down
    // insertPosition = targetPhaseIndex; // Keep it as is
        
    // Insert the item at the calculated position
    newSteps.splice(insertPosition, 0, draggedStep);
        
    console.log('🔧 Reorder calculation:', {
      originalDraggedIndex: draggedPhase,
      originalTargetIndex: targetPhaseIndex,
      insertPosition: insertPosition,
      direction: draggedPhase < targetPhaseIndex ? 'moving down' : 'moving up',
      beforeReorder: steps.map(s => s.name),
      afterReorder: newSteps.map(s => s.name)
    });
        
    console.log('✅ New order:', newSteps.map(s => s.name));
        
    setSteps(newSteps);
        
    // Auto-sync to localScriptGraph when auto-save is enabled
    if (autoSave) {
      setLocalScriptGraph(prev => ({
        ...prev,
        steps: newSteps
      }));
    }
        
    setDragOverPhase(null);
    setDraggedPhase(null);
    markDirty();
  };

  const handlePhaseDragEnd = () => {
    console.log('🏁 Phase drag end');
    setDraggedPhase(null);
    setDragOverPhase(null);
  };

  const handlePhaseDragEnter = (e, phaseIndex) => {
    e.preventDefault();
    console.log('🚪 Drag enter:', { draggedPhase, phaseIndex });
        
    if (draggedPhase !== null && draggedPhase !== phaseIndex) {
      setDragOverPhase(phaseIndex);
    }
  };

  const handlePhaseDragLeave = (e, phaseIndex) => {
    // Only clear if we're actually leaving this phase (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      console.log('👋 Drag leave:', phaseIndex);
      if (dragOverPhase === phaseIndex) {
        setDragOverPhase(null);
      }
    }
  };

  const handleNextNodeChange = (responseKey, value) => {
    if (value === 'ADD_NEW') {
      // Use enhanced node creator instead of simple text input
      setCurrentResponseKey(responseKey);
      setNodeCreationData({
        id: '',
        script: '',
        coaching: '',
        step: steps[0]?.id || defaultSteps[0]?.id || 'interest'
      });
      setShowEnhancedNodeCreator(true);
    } else if (value === '__end_script__') {
      // Handle end script option - this will trigger prospect data collection
      console.log('🏁 Setting response to end script and collect prospect data');
      handleUpdateResponse(responseKey, 'nextNode', '__end_script__');
      handleUpdateResponse(responseKey, 'isEndNode', true);
    } else {
      console.log('🔄 handleNextNodeChange called:', { responseKey, value });
      console.log('📝 Current editingResponseData keys:', Object.keys(editingResponseData));
      console.log('📝 Current response data exists:', !!editingResponseData[responseKey]);
            
      // Update the editing response data
      handleUpdateResponse(responseKey, 'nextNode', value);
            
      // If this is an open text response, update all other open text responses to use the same next node
      const currentResponse = editingResponseData[responseKey];
      if (currentResponse && currentResponse.type === 'open_text' && value) {
        Object.keys(editingResponseData).forEach(otherKey => {
          if (otherKey !== responseKey && editingResponseData[otherKey].type === 'open_text') {
            console.log(`🔄 Syncing next node for open text response ${otherKey} to ${value}`);
            handleUpdateResponse(otherKey, 'nextNode', value);
          }
        });
      }
            
      // Also update the localScriptGraph to ensure the change is persisted
      if (selectedNode) {
        setLocalScriptGraph(prev => {
          const updatedResponses = {
            ...prev.nodes[selectedNode.id].responses,
            [responseKey]: {
              ...prev.nodes[selectedNode.id].responses[responseKey],
              ...editingResponseData[responseKey], // Preserve all current editing data
              nextNode: value
            }
          };
                    
          // Also sync other open text responses in the graph
          Object.keys(updatedResponses).forEach(otherKey => {
            if (otherKey !== responseKey && updatedResponses[otherKey].type === 'open_text') {
              updatedResponses[otherKey].nextNode = value;
            }
          });
                    
          console.log('🔄 Updated responses for graph:', updatedResponses);
                    
          return {
            ...prev,
            nodes: {
              ...prev.nodes,
              [selectedNode.id]: {
                ...prev.nodes[selectedNode.id],
                responses: updatedResponses
              }
            }
          };
        });
      }
            
      setShowAddNodeOption(null);
      setTemplateDirty(true);
    }
  };

  const handleCreateNewNode = (responseKey) => {
    if (newNodeName.trim()) {
      const newNodeId = newNodeName.trim();
            
      // Validate the node name
      const validationError = validateNodeName(newNodeId);
      if (validationError) {
        showError(validationError);
        return;
      }
            
      // Add the new node to the script graph
      setLocalScriptGraph(prev => ({
        ...prev,
        nodes: {
          ...prev.nodes,
          [newNodeId]: {
            id: newNodeId,
            script: `Script for ${newNodeId}`,
            coaching: `Coaching for ${newNodeId}`,
            responses: {},
            step: steps[0]?.id || defaultSteps[0]?.id || 'interest' // Default to first step
          }
        }
      }));
            
      // Get the current response data to preserve all existing fields
      const currentResponse = editingResponseData[responseKey] || {};
            
      // Update the response to point to the new node while preserving all other fields
      const updatedResponse = {
        ...currentResponse,
        nextNode: newNodeId
      };
            
      // Update the editing response data
      setEditingResponseData(prev => ({
        ...prev,
        [responseKey]: updatedResponse
      }));
            
      // Also update the localScriptGraph to ensure the response change is persisted
      if (selectedNode) {
        setLocalScriptGraph(prev => ({
          ...prev,
          nodes: {
            ...prev.nodes,
            [selectedNode.id]: {
              ...prev.nodes[selectedNode.id],
              responses: {
                ...prev.nodes[selectedNode.id].responses,
                [responseKey]: updatedResponse
              }
            }
          }
        }));
      }
            
      setTemplateDirty(true);
      setShowAddNodeOption(null);
      setNewNodeName('');
      markDirty();
    }
  };

  // Enhanced node creation with template support
  const handleEnhancedNodeCreation = (action) => {
    if (!nodeCreationData.id.trim()) {
      showWarning('Please enter a node name');
      return;
    }

    const newNodeId = nodeCreationData.id.trim();
        
    // Validate the node name
    const validationError = validateNodeName(newNodeId);
    if (validationError) {
      showError(validationError);
      return;
    }

    // Use the script and coaching as entered
    const nodeScript = nodeCreationData.script;
    const nodeCoaching = nodeCreationData.coaching;

    // Create the new node
    const newNode = {
      id: newNodeId,
      script: nodeScript,
      coaching: nodeCoaching,
      responses: {},
      step: nodeCreationData.step,
      createdAt: Date.now(), // Add timestamp for sorting
      aiConfig: {
        canGenerateCustomScript: true,
        canAddCustomResponses: true,
        canSkipToAnyNode: false,
        contextWindow: 5,
        fallbackNode: 'objection_not_interested'
      }
    };

    // Add the new node to the script graph
    setLocalScriptGraph(prev => ({
      ...prev,
      nodes: {
        ...prev.nodes,
        [newNodeId]: newNode
      }
    }));

    // Update the current response to point to the new node
    if (currentResponseKey) {
      const currentResponse = editingResponseData[currentResponseKey] || {};
      const updatedResponse = {
        ...currentResponse,
        nextNode: newNodeId
      };

      setEditingResponseData(prev => ({
        ...prev,
        [currentResponseKey]: updatedResponse
      }));

      // Also update in the graph
      if (selectedNode) {
        setLocalScriptGraph(prev => ({
          ...prev,
          nodes: {
            ...prev.nodes,
            [selectedNode.id]: {
              ...prev.nodes[selectedNode.id],
              responses: {
                ...prev.nodes[selectedNode.id].responses,
                [currentResponseKey]: updatedResponse
              }
            }
          }
        }));
      }
    }

    setTemplateDirty(true);
    setShowEnhancedNodeCreator(false);
    markDirty();

    // Handle different actions
    if (action === 'create_and_edit') {
      // Add to editing path for breadcrumb navigation
      if (selectedNode && currentResponseKey) {
        // Coming from within a node's response - add current node to path
        setEditingPath(prev => [...prev, selectedNode.id]);
      } else if (selectedNode && !currentResponseKey) {
        // Coming from editing a node but not from a specific response - add current node to path
        setEditingPath(prev => [...prev, selectedNode.id]);
      } else {
        // Coming from flow view (no selectedNode) - start fresh path
        setEditingPath([]);
      }
            
      // Switch to editing the new node
      const safeNode = createSafeSelectedNode(newNode, newNodeId);
      safeNode.originalId = newNodeId;
      safeNode._createdFromResponse = currentResponseKey;
      safeNode._parentNode = selectedNode?.id;
      setSelectedNode(safeNode);
            
      // Show success message with context
      if (currentResponseKey) {
        const responseLabel = editingResponseData[currentResponseKey]?.label || currentResponseKey;
        showSuccess(`Node "${newNodeId}" created! Continuing from response: "${responseLabel}"`);
      } else {
        showSuccess(`Node "${newNodeId}" created! Now editing the new question.`);
      }
    } else {
      // Just create and return (create_and_return)
      showSuccess(`Node "${newNodeId}" created successfully!`);
    }
  };

  // Validate node name
  const validateNodeName = (name, excludeId = null) => {
    if (!name || name.trim().length === 0) {
      return 'Node name cannot be empty';
    }
        
    // Check for actual Firestore restrictions
    if (name.includes('/') || name.includes('.') || name.includes('..')) {
      return 'Node name cannot contain /, ., or .. (Firestore restriction)';
    }
        
    // Check for duplicate names (excluding the current node being edited)
    if (localScriptGraph.nodes[name] && name !== excludeId) {
      return `A node named "${name}" already exists`;
    }
        
    return null; // No validation errors
  };

  // Removed handleDataFieldChange and handleCreateNewDataField since data fields are now auto-generated

  // Generate data field name from label text
  const generateDataFieldName = (labelText) => {
    if (!labelText || !labelText.trim()) return '';
        
    // Convert label text to a valid data field name
    let dataFieldName = labelText
      .toLowerCase()
      .trim()
    // Replace spaces and common separators with underscores
      .replace(/[\s\-_]+/g, '_')
    // Remove special characters except underscores
      .replace(/[^a-z0-9_]/g, '')
    // Ensure it starts with a letter
      .replace(/^[^a-z]/, 'field_$&');
        
    // If empty after cleaning, use a default
    if (!dataFieldName) {
      dataFieldName = 'user_input';
    }
        
    // Ensure uniqueness by adding a number if needed
    let finalName = dataFieldName;
    let counter = 1;
    const existingDataFields = getExistingDataFields();
        
    while (existingDataFields.includes(finalName)) {
      finalName = `${dataFieldName}_${counter}`;
      counter++;
    }
        
    return finalName;
  };

  // Get all existing data fields from the script graph
  const getExistingDataFields = () => {
    const dataFields = new Set();
    Object.values(localScriptGraph?.nodes || {}).forEach(node => {
      if (node.responses) {
        Object.values(node.responses).forEach(response => {
          if (response.dataField) {
            dataFields.add(response.dataField);
          }
        });
      }
    });
    // Include data fields from editingResponseData to show current session additions
    Object.values(editingResponseData).forEach(response => {
      if (response.dataField) {
        dataFields.add(response.dataField);
      }
    });
    return Array.from(dataFields);
  };

  // Validate data field name
  const validateDataFieldName = (name) => {
    if (!name || name.trim().length === 0) {
      return 'Data field name cannot be empty';
    }
        
    // Check for actual Firestore restrictions
    if (name.includes('/') || name.includes('.') || name.includes('..')) {
      return 'Data field name cannot contain /, ., or .. (Firestore restriction)';
    }
        
    // Check for duplicate names
    const existingDataFields = getExistingDataFields();
    if (existingDataFields.includes(name)) {
      return `A data field named "${name}" already exists`;
    }
        
    return null; // No validation errors
  };


  // Group nodes by steps
  const groupedNodes = steps.reduce((acc, step) => {
    acc[step.id] = [];
    return acc;
  }, {});

  // Ensure groupedNodes has the expected structure for fallback logic
  // Always ensure these default categories exist for backward compatibility
  if (!groupedNodes.interest) groupedNodes.interest = [];
  if (!groupedNodes.motivation) groupedNodes.motivation = [];
  if (!groupedNodes.condition) groupedNodes.condition = [];

  // Group nodes by their assigned step, with fallback to the old logic
  if (localScriptGraph?.nodes) {
    const allNodeIds = Object.keys(localScriptGraph.nodes).filter(nodeId => nodeId && typeof nodeId === 'string');
    console.log('🔍 Grouping nodes by step:', {
      totalNodes: allNodeIds.length,
      nodeIds: allNodeIds,
      availableSteps: Object.keys(groupedNodes)
    });
        
    allNodeIds.forEach(nodeId => {
      const node = localScriptGraph.nodes[nodeId];
      const assignedStep = node.step;
            
      console.log(`  📌 Node "${nodeId}": step="${assignedStep}", stepExists=${!!groupedNodes[assignedStep]}`);
            
      if (assignedStep && groupedNodes[assignedStep]) {
        groupedNodes[assignedStep].push(nodeId);
      } else {
        // Fallback to the old logic for existing nodes without step assignment
        if (nodeId.includes('opening') || nodeId.includes('interest')) {
          groupedNodes.interest.push(nodeId);
        } else if (nodeId.includes('motivation') || nodeId.includes('timeline') || nodeId.includes('priority')) {
          groupedNodes.motivation.push(nodeId);
        } else {
          groupedNodes.condition.push(nodeId);
        }
      }
    });
  }

  const handleDeleteTemplate = (templateId) => {
    // All templates are now team-owned and can be deleted
    // (except read-only ones which are handled by disabled button)
    setTemplateToDelete(templateId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;
        
    try {
      await onDeleteTemplate(templateToDelete);
      setShowDeleteConfirm(false);
      setTemplateToDelete(null);
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const handleSetAsDefault = (templateId) => {
    console.log('ScriptBuilder: handleSetAsDefault called with templateId:', templateId);
    if (onSetAsDefault) {
      console.log('ScriptBuilder: Calling onSetAsDefault function');
      onSetAsDefault(templateId);
    } else {
      console.error('ScriptBuilder: onSetAsDefault function not provided');
    }
  };

  // highlightedNode, buildNodeConnections, findPreviousNodes removed — grid view replaced

  // Back from edit with confirm
  const handleBackWithConfirm = async () => {
    const activeName = displayTemplates[selectedTemplate]?.name || 'Template';
    if (!autoSave && (hasUnsavedChanges || templateDirty)) {
      const ok = await confirmDialog(
        hasUnsavedChanges
          ? 'Discard changes and go back?'
          : `You have unsaved changes to your Template: ${activeName}. Go back without saving?`,
        { variant: 'danger', confirmText: 'Go Back' }
      );
      if (!ok) return;
    }
        
    // Handle breadcrumb navigation
    if (editingPath.length > 0) {
      // Go back to the previous node in the path
      const previousNodeId = editingPath[editingPath.length - 1];
      const previousNode = localScriptGraph.nodes[previousNodeId];
            
      if (previousNode) {
        setEditingPath(prev => prev.slice(0, -1)); // Remove last item from path
        const safeNode = createSafeSelectedNode(previousNode, previousNodeId);
        safeNode.originalId = previousNodeId;
        setSelectedNode(safeNode);
        // Stay in edit mode to continue the flow
        return;
      }
    }
        
    // Default: close the side panel
    setEditingPath([]);
    setSelectedNode(null);
    setEditingResponseData({});
  };

  // Navigate directly to a specific node in the breadcrumb path
  const handleBreadcrumbNavigation = (nodeId, pathIndex) => {
    const targetNode = localScriptGraph.nodes[nodeId];
    if (!targetNode) return;

    // Trim the editing path to the selected point
    setEditingPath(prev => prev.slice(0, pathIndex));
        
    // Switch to editing the target node
    const safeNode = createSafeSelectedNode(targetNode, nodeId);
    safeNode.originalId = nodeId;
    setSelectedNode(safeNode);
  };

  // Loading check - ensure we have the data we need
  if (loading || !isInitialized || !availableTemplates || !currentTemplate || !displayTemplates[currentTemplate]) {
    console.log('⏳ ScriptBuilder initializing...');
    return (
      <div className="text-center py-4">
        <LoadingSpinner size="medium" message="Loading..." />
      </div>
    );
  }

  // runAutoLayout removed — layout handled in FlowChartEditor

  return (
    <div ref={scrollContainerRef} className="w-full flex flex-col" style={{ padding: spacing.md, opacity: isInitialized ? 1 : 0, transition: 'opacity 0.2s' }}>
      {/* Compact Header Bar */}
      <div className="flex justify-between items-center mb-3">
        {/* Left: Auto-Save */}
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <div
              onClick={() => {
                const template = displayTemplates[selectedTemplate];
                const isDisabled = !template || template.isReadOnly;
                if (!isDisabled) handleAutoSaveToggle(!autoSave);
              }}
              className={`relative inline-flex items-center h-5 rounded-full w-9 transition-colors ${
                autoSave ? 'bg-blue-500' : 'bg-gray-300'
              } ${
                !displayTemplates[selectedTemplate] || displayTemplates[selectedTemplate]?.isReadOnly
                  ? 'cursor-not-allowed opacity-50' 
                  : 'cursor-pointer'
              }`}
            >
              <span
                className={`inline-block w-3.5 h-3.5 transform bg-white rounded-full transition-transform ${
                  autoSave ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </div>
            Auto-save
          </label>
          {autoSave && autoSaveStatus && (
            <span className="text-xs text-gray-400">{autoSaveStatus}</span>
          )}
        </div>

      </div>


      {/* Script Management - Simplified */}
      {viewMode !== 'edit' && (
        <div style={{ marginBottom: spacing.sm }}>
          <div className="flex items-center justify-between gap-3">
            {/* Left: Script Selector + Info */}
            <div className="flex items-center gap-2">
              <select
                value={selectedTemplate}
                onChange={(e) => handleLoadTemplate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white truncate"
                style={{ width: '260px', flexShrink: 0 }}
              >
                {Object.entries(displayTemplates)
                  .map(([id, template]) => (
                    <option key={id} value={id}>
                      {template.name || id}
                    </option>
                  ))
                }
              </select>

              {/* Default star — always visible, amber when default, gray when not */}
              {!displayTemplates[selectedTemplate]?.isReadOnly && (
                selectedTemplate === defaultTemplate ? (
                  <span
                    title="This is your default script"
                    className="flex items-center justify-center w-7 h-7 text-amber-400 cursor-default flex-shrink-0"
                    aria-label="Default script"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </span>
                ) : (
                  <button
                    onClick={() => handleSetAsDefault(selectedTemplate)}
                    title="Set as default script"
                    className="flex items-center justify-center w-7 h-7 text-gray-300 hover:text-amber-400 transition-colors flex-shrink-0 rounded"
                    aria-label="Set as default script"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 20 20">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </button>
                )
              )}
              
              {/* Node count */}
              <span className="text-xs text-gray-400 flex-shrink-0">
                {Object.keys(localScriptGraph?.nodes || {}).length} nodes
              </span>

              {/* Revision History button */}
              <button
                onClick={() => setShowRevisionHistory(true)}
                title="View revision history"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  fontSize: typography.small.size,
                  color: colors.neutral[600],
                  background: 'white',
                  border: `1px solid ${colors.neutral[300]}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.neutral[50]; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
              >
                <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                History
              </button>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {/* Save Current Script */}
              {!displayTemplates[selectedTemplate]?.isReadOnly && templateDirty && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowSaveConfirm(true)}
                  title="Save changes to current script"
                >
                  Save
                </Button>
              )}

              {/* Save as New */}
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowTemplateDialog(true)}
                title="Save as new script"
              >
                Save As...
              </Button>

              {/* More Actions Dropdown - Click to toggle */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  title="More actions"
                  aria-label="More actions"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </Button>
                {showActionsMenu && (
                  <>
                    {/* Backdrop to close menu */}
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowActionsMenu(false)}
                    />
                    <div className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                      <button
                        onClick={() => {
                          handleEditTemplate(selectedTemplate);
                          setShowActionsMenu(false);
                        }}
                        disabled={displayTemplates[selectedTemplate]?.isReadOnly}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        Rename
                      </button>
                      <button
                        onClick={() => {
                          handleDeleteTemplate(selectedTemplate);
                          setShowActionsMenu(false);
                        }}
                        disabled={displayTemplates[selectedTemplate]?.isReadOnly || !displayTemplates[selectedTemplate]?.canEdit}
                        className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Script Validation Panel - Collapsible */}
      {viewMode !== 'edit' && (() => {
        const validation = validateScriptFlow();
        const hasIssues = !validation.isValid || validation.startNodes.length !== 1;
        
        return (
          <div style={{ marginBottom: spacing.sm }}>
            {/* Collapsed view - just a status indicator */}
            <button
              onClick={() => setValidationExpanded(!validationExpanded)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                validation.isValid 
                  ? 'bg-green-50 hover:bg-green-100 border border-green-200' 
                  : 'bg-yellow-50 hover:bg-yellow-100 border border-yellow-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${validation.isValid ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                <span className={`font-medium ${validation.isValid ? 'text-green-700' : 'text-yellow-700'}`}>
                  {validation.isValid ? '✓ Script Complete' : '⚠ Needs Attention'}
                </span>
                {!validation.isValid && (
                  <span className="text-yellow-600 text-xs">
                    ({validation.incompleteNodes.length + validation.brokenConnections.length + (validation.unreachableNodes?.length || 0)} issues)
                  </span>
                )}
              </div>
              <svg 
                className={`w-4 h-4 text-gray-500 transition-transform ${validationExpanded ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* Expanded view - full details */}
            {validationExpanded && (
              <div className={`mt-2 px-3 py-2 rounded-lg text-sm ${
                validation.isValid ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <div className="text-xs text-gray-600 mb-2">
                  Start: {validation.startNodes.length} | End: {validation.endNodes.length} | Incomplete: {validation.incompleteNodes.length} | Broken: {validation.brokenConnections.length} | Unreachable: {validation.unreachableNodes?.length || 0}
                </div>
                {hasIssues && (
                  <div className="space-y-1">
                    {validation.startNodes.length === 0 && (
                      <div className="text-red-600 text-xs">
                        ❌ No start node - designate one as opening question
                      </div>
                    )}
                    {validation.startNodes.length > 1 && (
                      <div className="text-orange-600 text-xs">
                        ⚠️ Multiple start nodes: {validation.startNodes.slice(0, 3).join(', ')}{validation.startNodes.length > 3 ? '...' : ''}
                      </div>
                    )}
                    {validation.endNodes.length === 0 && (
                      <div className="text-red-600 text-xs">❌ No end node - add script ending</div>
                    )}
                    {validation.incompleteNodes.length > 0 && (
                      <div className="text-red-600 text-xs">
                        ❌ Incomplete: {validation.incompleteNodes.slice(0, 3).join(', ')}{validation.incompleteNodes.length > 3 ? '...' : ''}
                      </div>
                    )}
                    {validation.brokenConnections.length > 0 && (
                      <div className="text-red-600 text-xs">
                        ❌ Broken links: {validation.brokenConnections.length} connection(s)
                      </div>
                    )}
                    {validation.unreachableNodes?.length > 0 && (
                      <div className="text-red-600 text-xs">
                        ❌ Unreachable: {validation.unreachableNodes.slice(0, 3).join(', ')}{validation.unreachableNodes.length > 3 ? '...' : ''}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Flowchart Snapshot Preview - Real ReactFlow thumbnail */}
      {viewMode !== 'edit' && localScriptGraph?.nodes && (
        <MiniFlowchartPreview
          graph={localScriptGraph}
          steps={steps}
          onOpenFlowchart={async () => {
            if (templateDirty) {
              await handleSaveChangesDirectly();
            }
            navigate(`/flowchart-editor/${selectedTemplate}?t=${Date.now()}`);
          }}
        />
      )}

      {/* Sub-tab Navigation */}
      {viewMode !== 'edit' && localScriptGraph?.nodes && (
        <div className="flex border-b border-gray-200 mb-3">
          {[
            { id: 'nodes', label: 'Nodes', badge: Object.keys(localScriptGraph.nodes).filter(id => !id.startsWith('objection_')).length },
            { id: 'phases', label: 'Phases', badge: steps.length },
            { id: 'objections', label: 'Objections', badge: steps.reduce((n, s) => n + (s.objectionResponses?.length || 0), 0) || null },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveBuilderTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeBuilderTab === tab.id
                  ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              {tab.label}
              {tab.badge != null && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeBuilderTab === tab.id ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Nodes Tab */}
      {viewMode !== 'edit' && activeBuilderTab === 'nodes' && localScriptGraph?.nodes && (() => {
        const validation = validateScriptFlow();
        const allNodeIds = Object.keys(localScriptGraph.nodes).filter(
          id => id && typeof id === 'string' && !id.startsWith('objection_obj_') && !id.startsWith('objection_')
        );
        
        // Filter by search (name, phase, or script text)
        const filteredNodes = allNodeIds.filter(id => {
          if (!nodeSearchQuery) return true;
          const q = nodeSearchQuery.toLowerCase();
          const node = localScriptGraph.nodes[id];
          return id.toLowerCase().includes(q) || (node.step || '').toLowerCase().includes(q) || (node.script || '').toLowerCase().includes(q);
        });

        // Sort nodes
        const sortedNodes = [...filteredNodes].sort((a, b) => {
          const nodeA = localScriptGraph.nodes[a];
          const nodeB = localScriptGraph.nodes[b];
          if (nodeSortBy === 'phase') {
            return (nodeA.step || '').localeCompare(nodeB.step || '');
          }
          if (nodeSortBy === 'status') {
            const aIsIncomplete = validation.incompleteNodes.includes(a) || validation.unreachableNodes?.includes(a);
            const bIsIncomplete = validation.incompleteNodes.includes(b) || validation.unreachableNodes?.includes(b);
            if (aIsIncomplete !== bIsIncomplete) return aIsIncomplete ? -1 : 1;
          }
          // Default: sort by name
          return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
        });

        return (
          <div className="mb-4 border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                <span className="text-sm font-semibold text-gray-700">Nodes</span>
                <span className="text-xs text-gray-400">{allNodeIds.length} total</span>
              </div>
              <div className="flex items-center gap-2">
                {/* Sort selector */}
                <select
                  value={nodeSortBy}
                  onChange={(e) => setNodeSortBy(e.target.value)}
                  className="text-xs border border-gray-200 rounded px-1.5 py-1 text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-blue-300"
                >
                  <option value="name">Sort: Name</option>
                  <option value="phase">Sort: Phase</option>
                  <option value="status">Sort: Status</option>
                </select>
              </div>
            </div>
            
            {/* Search bar */}
            <div className="px-3 py-2 border-b border-gray-100">
              <input
                type="text"
                value={nodeSearchQuery}
                onChange={(e) => setNodeSearchQuery(e.target.value)}
                placeholder="Search nodes..."
                className="w-full text-sm px-3 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-blue-300"
              />
            </div>

            {/* Table header */}
            <div className="grid grid-cols-[minmax(70px,1fr)_minmax(50px,0.6fr)_minmax(100px,2fr)_40px_minmax(80px,1.2fr)] gap-1 px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
              <div>Node</div>
              <div>Phase</div>
              <div>Question</div>
              <div className="text-center">Resp.</div>
              <div>Next</div>
            </div>

            {/* Node rows */}
            <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
              {sortedNodes.length === 0 ? (
                <div className="px-4 py-4 text-center text-sm text-gray-400">
                  {nodeSearchQuery ? 'No nodes match your search' : 'No nodes yet — use the Flowchart Editor to add nodes'}
                </div>
              ) : (
                sortedNodes.map((nodeId) => {
                  const node = localScriptGraph.nodes[nodeId];
                  const isOpening = node.isOpeningNode;
                  const isIncomplete = validation.incompleteNodes.includes(nodeId);
                  const isUnreachable = validation.unreachableNodes?.includes(nodeId);
                  const responses = node.responses ? Object.entries(node.responses) : [];
                  const responseCount = responses.length;

                  // Script preview — first ~40 chars
                  const scriptPreview = typeof node.script === 'string' && node.script.trim()
                    ? node.script.replace(/\n/g, ' ').trim().substring(0, 50) + (node.script.length > 50 ? '...' : '')
                    : 'No script text';

                  // Next questions list
                  const nextNodes = responses
                    .map(([, resp]) => resp.nextNode)
                    .filter(Boolean)
                    .map(n => n === '__end_script__' ? 'End' : n);

                  return (
                    <div
                      key={nodeId}
                      onClick={() => handleEditNode(nodeId)}
                      className={`grid grid-cols-[minmax(70px,1fr)_minmax(50px,0.6fr)_minmax(100px,2fr)_40px_minmax(80px,1.2fr)] gap-1 px-3 py-1.5 cursor-pointer hover:bg-gray-50 border-b border-gray-50 transition-colors items-center ${selectedNode?.id === nodeId || selectedNode?.originalId === nodeId ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''}`}
                      title={`Click to edit ${nodeId}`}
                    >
                      {/* Node name + badges */}
                      <div className="flex items-center gap-1 min-w-0">
                        <span className="text-xs font-semibold text-gray-800 truncate" title={nodeId}>{nodeId}</span>
                        {isOpening && <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-green-500" title="Start node" />}
                        {isIncomplete && <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-red-500" title="Incomplete" />}
                        {isUnreachable && <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-yellow-500" title="Unreachable" />}
                      </div>

                      {/* Phase badge */}
                      <div className="min-w-0">
                        {node.step ? (
                          <span className="inline-block text-[10px] px-1.5 py-0.5 rounded-full truncate bg-blue-50 text-blue-700 border border-blue-200 max-w-full" title={node.step}>
                            {node.step}
                          </span>
                        ) : (
                          <span className="text-[10px] text-gray-300">—</span>
                        )}
                      </div>

                      {/* Question preview */}
                      <div className="text-xs text-gray-500 truncate min-w-0" title={node.script || ''}>
                        {scriptPreview}
                      </div>

                      {/* Response count */}
                      <div className="text-xs text-gray-600 text-center font-medium">
                        {responseCount}
                      </div>

                      {/* Next questions */}
                      <div className="flex items-center gap-1 min-w-0 overflow-hidden">
                        {nextNodes.length === 0 ? (
                          <span className="text-xs text-gray-300">—</span>
                        ) : (
                          nextNodes.slice(0, 2).map((n, i) => (
                            <span key={i} className={`text-[10px] px-1 py-0.5 rounded truncate ${n === 'End' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'}`} style={{ maxWidth: '70px' }} title={n}>
                              {n}
                            </span>
                          ))
                        )}
                        {nextNodes.length > 2 && (
                          <span className="text-[10px] text-gray-400">+{nextNodes.length - 2}</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })()}

      {/* Phases Tab */}
      {viewMode !== 'edit' && activeBuilderTab === 'phases' && (
        <div className="mb-4 border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden">
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">
                {editingSteps ? 'Drag to reorder, click names to rename' : 'Click "Edit" to rename, reorder, add, or remove phases'}
              </span>
              <Button
                variant={editingSteps ? 'secondary' : 'primary'}
                size="sm"
                onClick={handleEditPhasesToggle}
              >
                {editingSteps ? '✓ Done' : 'Edit'}
              </Button>
            </div>

            {steps.map((step, stepIndex) => {
              const nodesInPhase = (groupedNodes[step.id] || []).filter(
                id => !id.startsWith('objection_obj_') && !id.startsWith('objection_')
              );
              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                    dragOverPhase === stepIndex ? 'border-blue-400 bg-blue-50' : 'border-gray-100 bg-gray-50'
                  } ${draggedPhase === stepIndex ? 'opacity-50' : ''}`}
                  onDragEnter={(e) => editingSteps && handlePhaseDragEnter(e, stepIndex)}
                  onDragOver={(e) => editingSteps && handlePhaseDragOver(e, stepIndex)}
                  onDragLeave={(e) => editingSteps && handlePhaseDragLeave(e, stepIndex)}
                  onDrop={(e) => editingSteps && handlePhaseDrop(e, stepIndex)}
                >
                  {editingSteps && (
                    <div
                      className="cursor-move hover:bg-gray-200 rounded p-0.5"
                      draggable={true}
                      onDragStart={(e) => handlePhaseDragStart(e, stepIndex)}
                      onDragEnd={handlePhaseDragEnd}
                      title="Drag to reorder"
                    >
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                    </div>
                  )}
                  {editingSteps ? (
                    <input
                      type="text"
                      value={step.name}
                      onChange={(e) => handleUpdateStep(stepIndex, 'name', e.target.value)}
                      className="flex-1 text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-300"
                    />
                  ) : (
                    <span className="flex-1 text-sm font-medium text-gray-700">{step.name}</span>
                  )}
                  <span className="text-xs text-gray-400">{nodesInPhase.length} nodes</span>
                  {editingSteps && nodesInPhase.length === 0 && (
                    <button
                      onClick={() => handleRemoveStep(stepIndex)}
                      className="text-red-400 hover:text-red-600 p-0.5"
                      title="Delete empty phase"
                      aria-label="Delete empty phase"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              );
            })}

            {editingSteps && (
              <button
                onClick={handleAddStep}
                className="w-full text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 py-2 rounded-lg border border-dashed border-blue-300 transition-colors"
              >
                + Add Phase
              </button>
            )}
          </div>
        </div>
      )}

      {/* Objections Tab */}
      {viewMode !== 'edit' && activeBuilderTab === 'objections' && (
        <div className="mb-4 space-y-4">
          {steps.length === 0 ? (
            <div className="text-sm text-gray-400 text-center py-6">No phases defined yet.</div>
          ) : steps.map((step) => {
            const objections = step.objectionResponses || [];
            const isEditing = editingObjections[step.id];
            return (
              <div key={step.id} className="border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{step.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{objections.length} objection{objections.length !== 1 ? 's' : ''}</span>
                    {objections.length > 0 && (
                      <button
                        onClick={() => toggleEditingObjections(step.id)}
                        className="text-xs text-blue-500 hover:text-blue-700"
                      >
                        {isEditing ? 'Done' : 'Edit'}
                      </button>
                    )}
                  </div>
                </div>
                <div className="p-3 space-y-2">
                  {objections.length === 0 ? (
                    <div className="text-xs text-gray-400 py-1">No objections — add one below</div>
                  ) : (
                    objections.map((obj, objIndex) => (
                      <div key={obj.id || objIndex} className="flex items-start gap-2 px-3 py-2 rounded bg-red-50 border border-red-100">
                        {isEditing ? (
                          <div className="flex-1 space-y-1">
                            <input
                              type="text"
                              value={obj.label}
                              onChange={(e) => handleEditObjection(step.id, objIndex, 'label', e.target.value)}
                              className="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-300"
                              placeholder="Objection label"
                            />
                            <textarea
                              value={obj.response}
                              onChange={(e) => handleEditObjection(step.id, objIndex, 'response', e.target.value)}
                              className="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-300"
                              rows={2}
                              placeholder="Response text"
                            />
                          </div>
                        ) : (
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-medium text-red-700">{obj.label}</span>
                            <p className="text-xs text-gray-600 truncate">{obj.response}</p>
                          </div>
                        )}
                        {isEditing && (
                          <button
                            onClick={() => handleRemoveObjection(step.id, objIndex)}
                            className="text-red-400 hover:text-red-600 p-0.5 flex-shrink-0"
                            title="Delete objection"
                            aria-label="Delete objection"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))
                  )}
                  {showAddObjection === step.id ? (
                    <div className="px-3 py-2 rounded bg-gray-50 border border-gray-200 space-y-2">
                      <input
                        type="text"
                        value={newObjectionData.label}
                        onChange={(e) => setNewObjectionData(prev => ({ ...prev, label: e.target.value }))}
                        placeholder="Objection label (e.g. 'Not interested')"
                        className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-300"
                      />
                      <textarea
                        value={newObjectionData.response}
                        onChange={(e) => setNewObjectionData(prev => ({ ...prev, response: e.target.value }))}
                        placeholder="Agent response to this objection..."
                        className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-300"
                        rows={2}
                      />
                      <div className="flex gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleSaveObjection(step.id)}
                          disabled={!newObjectionData.label.trim() || !newObjectionData.response.trim()}
                        >
                          Save
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setShowAddObjection(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleAddObjection(step.id)}
                      className="text-xs text-blue-500 hover:text-blue-700 px-1 py-1"
                    >
                      + Add Objection
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}


      {/* Save Script Dialog */}
      {showTemplateDialog && (
        <Modal
          isOpen={showTemplateDialog}
          onClose={() => setShowTemplateDialog(false)}
          title={editingTemplateId ? 'Rename Script' : 'Save as New Script'}
          size="sm"
        >
            {!editingTemplateId && (
              <p className="text-sm text-gray-600 mb-4">
                This will create a copy of your current script with a new name.
              </p>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {editingTemplateId ? 'New Name' : 'Script Name'}
              </label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => {
                  setTemplateName(e.target.value);
                  setTemplateNameError(''); // Clear error when typing
                }}
                placeholder="Enter a name for your script"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {templateNameError && (
                <p className="text-red-500 text-xs mt-1">{templateNameError}</p>
              )}
            </div>
            <Modal.Footer>
              <Button
                variant="outline"
                onClick={() => setShowTemplateDialog(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleApplyChanges}
                disabled={isSaving || !templateName.trim()}
                loading={isSaving}
              >
                {isSaving ? 'Saving...' : (editingTemplateId ? 'Rename' : 'Save Copy')}
              </Button>
            </Modal.Footer>
        </Modal>
      )}

      {/* Save Confirmation Dialog */}
      {showSaveConfirm && (
        <Modal
          isOpen={showSaveConfirm}
          onClose={() => setShowSaveConfirm(false)}
          title="Save Changes"
          size="sm"
        >
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-sm text-amber-800">
                  This will overwrite <strong>&quot;{displayTemplates[selectedTemplate]?.name}&quot;</strong> with your current changes.
                </p>
              </div>
              <p className="text-sm text-gray-600">
                This action cannot be undone. Are you sure you want to continue?
              </p>
            </div>
            <Modal.Footer>
              <Button
                variant="outline"
                onClick={() => setShowSaveConfirm(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  setShowSaveConfirm(false);
                  await handleSaveChangesDirectly();
                }}
                disabled={isSaving}
                loading={isSaving}
              >
                {isSaving ? 'Saving...' : 'Overwrite Script'}
              </Button>
            </Modal.Footer>
        </Modal>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <Modal
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setTemplateToDelete(null);
          }}
          title="Delete Script"
          size="sm"
        >
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete &quot;{displayTemplates[templateToDelete]?.name}&quot;? This action cannot be undone.
            </p>
            <Modal.Footer>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setTemplateToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={confirmDeleteTemplate}
              >
                Delete
              </Button>
            </Modal.Footer>
        </Modal>
      )}

      {/* Node Delete Confirmation Dialog */}
      {showDeleteNodeConfirm && (
        <Modal
          isOpen={showDeleteNodeConfirm}
          onClose={() => {
            setShowDeleteNodeConfirm(false);
            setNodeToDelete(null);
          }}
          title="Delete Node"
          size="sm"
        >
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete node "{nodeToDelete}"? This action cannot be undone and will remove all connections to this node.
            </p>
            <Modal.Footer>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteNodeConfirm(false);
                  setNodeToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={confirmDeleteNode}
              >
                Delete
              </Button>
            </Modal.Footer>
        </Modal>
      )}

      {/* Enhanced Node Creator Modal */}
      {showEnhancedNodeCreator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{zIndex: 9999}}>
          <div className="bg-white rounded-lg p-4 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                Create New Question
            </h3>
            <p className="text-gray-600 mb-4">
                                What happens next when they give this response?
            </p>
                            
            <div className="space-y-4">
              {/* Node Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Question ID *
                </label>
                <input
                  type="text"
                  value={nodeCreationData.id}
                  onChange={(e) => setNodeCreationData(prev => ({ ...prev, id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., price_objection, motivation_check, price_objection_handler"
                />
              </div>



              {/* Script Phase */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Script Phase
                </label>
                <select
                  value={nodeCreationData.step}
                  onChange={(e) => setNodeCreationData(prev => ({ ...prev, step: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {steps.map((step) => (
                    <option key={step.id} value={step.id}>
                      {step.name} - {step.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Script */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Script
                </label>
                <textarea
                  value={nodeCreationData.script}
                  onChange={(e) => setNodeCreationData(prev => ({ ...prev, script: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="What will you say?"
                />
              </div>

              {/* Coaching */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Coaching Notes
                </label>
                <textarea
                  value={nodeCreationData.coaching}
                  onChange={(e) => setNodeCreationData(prev => ({ ...prev, coaching: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Tips for handling this conversation..."
                />
              </div>
            </div>

            <div className="flex space-x-2 mt-6">
              <Button
                variant="primary"
                onClick={() => handleEnhancedNodeCreation('create_and_edit')}
                icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>}
              >
                Create & Continue Editing
              </Button>
              <Button
                variant="secondary"
                onClick={() => handleEnhancedNodeCreation('create_and_return')}
                icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
              >
                Create & Return
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowEnhancedNodeCreator(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Node Edit Side Panel */}
      {selectedNode && viewMode !== 'edit' && (() => {
        const panelNodeId = selectedNode.id;
        const isEndScript = selectedNode.isEndScript;
        const allNodeIds = Object.keys(localScriptGraph?.nodes || {}).filter(id => id !== panelNodeId);
        const responseKeys = Object.keys(editingResponseData).sort((a, b) => {
          const aNum = parseInt(a.replace('response_', ''));
          const bNum = parseInt(b.replace('response_', ''));
          return aNum - bNum;
        });

        return (
          <>
            {/* Backdrop overlay */}
            <div
              onClick={() => {
                if (hasUnsavedChanges && !autoSave) {
                  confirmDialog('Discard unsaved changes?', { variant: 'danger', confirmText: 'Discard' }).then(ok => {
                    if (ok) { setSelectedNode(null); setEditingResponseData({}); setHasUnsavedChanges(false); }
                  });
                } else {
                  setSelectedNode(null); setEditingResponseData({});
                }
              }}
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                zIndex: zIndex.modalBackdrop
              }}
              aria-hidden="true"
            />

            {/* Panel */}
            <div
              tabIndex={-1}
              role="dialog"
              aria-label="Edit node"
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: '520px',
                maxWidth: '92vw',
                height: '100vh',
                backgroundColor: '#fff',
                boxShadow: shadows.xl,
                zIndex: zIndex.modal,
                display: 'flex',
                flexDirection: 'column',
                outline: 'none'
              }}
            >
              {/* Header */}
              <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                  <svg width="20" height="20" fill="none" stroke="#6b7280" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {isEndScript ? 'Edit End Script' : `Edit: ${panelNodeId}`}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    if (hasUnsavedChanges && !autoSave) {
                      confirmDialog('Discard unsaved changes?', { variant: 'danger', confirmText: 'Discard' }).then(ok => {
                        if (ok) { setSelectedNode(null); setEditingResponseData({}); setHasUnsavedChanges(false); }
                      });
                    } else {
                      setSelectedNode(null); setEditingResponseData({});
                    }
                  }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '6px', color: '#9ca3af', fontSize: '20px', lineHeight: 1 }}
                  onMouseEnter={e => { e.target.style.color = '#374151'; e.target.style.background = '#f3f4f6'; }}
                  onMouseLeave={e => { e.target.style.color = '#9ca3af'; e.target.style.background = 'none'; }}
                  title="Close"
                >
                  ✕
                </button>
              </div>

              {/* Scrollable Content */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                {/* Question ID */}
                {!isEndScript && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>Question ID</label>
                    <input
                      type="text"
                      value={selectedNode.id}
                      onChange={(e) => {
                        const newId = e.target.value;
                        const validationError = validateNodeName(newId, selectedNode.originalId || selectedNode.id);
                        if (validationError) { showError(validationError); return; }
                        setSelectedNode(prev => ({ ...prev, id: newId }));
                        markDirty();
                      }}
                      onBlur={handleNodeIdRename}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                      placeholder="e.g., price_objection"
                    />
                  </div>
                )}

                {/* Script Phase */}
                {!isEndScript && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>Script Phase</label>
                    <select
                      value={selectedNode.step || ''}
                      onChange={(e) => {
                        const newStep = e.target.value;
                        setSelectedNode(prev => ({ ...prev, step: newStep }));
                        if (autoSave) {
                          setLocalScriptGraph(prev => ({
                            ...prev,
                            nodes: { ...prev.nodes, [panelNodeId]: { ...prev.nodes[panelNodeId], step: newStep } }
                          }));
                        }
                        markDirty();
                      }}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', background: 'white' }}
                    >
                      <option value="">-- Select Phase --</option>
                      {steps.map((step) => (
                        <option key={step.id} value={step.id}>{step.name} - {step.description}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Opening Node */}
                {!isEndScript && (
                  <div style={{
                    marginBottom: '16px',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: `1px solid ${selectedNode.isOpeningNode ? '#bbf7d0' : '#e5e7eb'}`,
                    background: selectedNode.isOpeningNode ? '#f0fdf4' : '#f9fafb'
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px' }}>
                      <input
                        type="checkbox"
                        checked={selectedNode.isOpeningNode || false}
                        onChange={(e) => {
                          setSelectedNode(prev => ({ ...prev, isOpeningNode: e.target.checked }));
                          if (e.target.checked) {
                            setLocalScriptGraph(prev => {
                              const updatedNodes = {};
                              Object.entries(prev.nodes).forEach(([nid, node]) => {
                                updatedNodes[nid] = { ...node, isOpeningNode: nid === panelNodeId };
                              });
                              return { ...prev, nodes: updatedNodes };
                            });
                          } else {
                            setLocalScriptGraph(prev => ({
                              ...prev,
                              nodes: { ...prev.nodes, [panelNodeId]: { ...prev.nodes[panelNodeId], isOpeningNode: false } }
                            }));
                          }
                          markDirty();
                        }}
                        style={{ width: '16px', height: '16px', accentColor: '#22c55e' }}
                      />
                      <span style={{ fontWeight: '500', color: '#374151' }}>This is the Starting Node</span>
                    </label>
                    <p style={{ margin: '4px 0 0 26px', fontSize: '12px', color: '#6b7280' }}>The call will begin at this node. Only one node can be the starting node.</p>
                  </div>
                )}

                {/* What You'll Say */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    {isEndScript ? 'Closing Statement' : "What You'll Say"}
                  </label>
                  <textarea
                    value={selectedNode.script || ''}
                    onChange={(e) => {
                      setSelectedNode(prev => ({ ...prev, script: e.target.value }));
                      if (autoSave) {
                        if (isEndScript) {
                          setLocalScriptGraph(prev => ({ ...prev, endScriptConfig: { ...prev.endScriptConfig, script: e.target.value } }));
                        } else {
                          setLocalScriptGraph(prev => ({ ...prev, nodes: { ...prev.nodes, [panelNodeId]: { ...prev.nodes[panelNodeId], script: e.target.value } } }));
                        }
                      }
                      markDirty();
                    }}
                    rows={4}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', minHeight: '96px' }}
                    placeholder="What will you say to the prospect at this point?"
                  />
                </div>

                {/* Tips & Coaching Notes */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    {isEndScript ? 'Transition Notes' : 'Tips & Coaching Notes'}
                  </label>
                  <textarea
                    value={selectedNode.coaching || ''}
                    onChange={(e) => {
                      setSelectedNode(prev => ({ ...prev, coaching: e.target.value }));
                      if (autoSave) {
                        if (isEndScript) {
                          setLocalScriptGraph(prev => ({ ...prev, endScriptConfig: { ...prev.endScriptConfig, coaching: e.target.value } }));
                        } else {
                          setLocalScriptGraph(prev => ({ ...prev, nodes: { ...prev.nodes, [panelNodeId]: { ...prev.nodes[panelNodeId], coaching: e.target.value } } }));
                        }
                      }
                      markDirty();
                    }}
                    rows={3}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', minHeight: '72px' }}
                    placeholder="Tips for handling this part of the conversation..."
                  />
                </div>

                {/* Responses Table */}
                {!isEndScript && (() => {
                  const scriptTargetOptions = [
                    { id: '', label: 'Select...' },
                    ...allNodeIds.map(nid => ({ id: nid, label: nid })),
                    { id: '__end_script__', label: 'End Script' },
                  ];

                  const normalizedResponses = responseKeys.map(key => ({
                    id: key,
                    label: editingResponseData[key]?.label || '',
                    type: editingResponseData[key]?.type || 'predetermined',
                    target: editingResponseData[key]?.nextNode || '',
                  }));

                  return (
                    <NodeResponsesTable
                      responses={normalizedResponses}
                      targetOptions={scriptTargetOptions}
                      onUpdateLabel={(id, val) => handleUpdateResponse(id, 'label', val)}
                      onUpdateType={(id, val) => handleUpdateResponse(id, 'type', val)}
                      onUpdateTarget={(id, val) => handleNextNodeChange(id, val)}
                      onRemove={(id) => { handleRemoveResponse(id); markDirty(); }}
                      onAdd={handleAddResponse}
                    />
                  );
                })()}
              </div>

              {/* Footer */}
              <div style={{
                padding: '12px 20px',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
                background: '#f9fafb'
              }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (templateDirty) { await handleSaveChangesDirectly(); }
                    const nodeId = selectedNode?.id || initialNodeId;
                    navigate(`/flowchart-editor/${selectedTemplate}?node=${nodeId}&t=${Date.now()}`);
                  }}
                  title="View this node in the Flowchart Editor"
                >
                  Show in Flowchart
                </Button>
                {autoSave ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setSelectedNode(null); setEditingResponseData({}); setHasUnsavedChanges(false); }}
                  >
                    Close
                  </Button>
                ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSelectedNode(null); setEditingResponseData({}); setHasUnsavedChanges(false); }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSaveNode}
                    >
                      Save Changes
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>
        );
      })()}

      {/* Add Node */}
      {viewMode === 'add' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">Create New Question</h3>
                        
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Question ID</label>
            <input
              type="text"
              value={newNodeData.id || ''}
              onChange={(e) => { setNewNodeData({...newNodeData, id: e.target.value}); markDirty(); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., price_objection, motivation_check"
            />
          </div>
                        
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Script Phase</label>
            <select
              value={newNodeData.step || 'interest'}
              onChange={(e) => { setNewNodeData({...newNodeData, step: e.target.value}); markDirty(); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {steps.map((step, index) => (
                <option key={step.id} value={step.id}>
                  {step.name} - {step.description}
                </option>
              ))}
            </select>
          </div>
                        
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">What You'll Say</label>
            <textarea
              value={newNodeData.script || ''}
              onChange={(e) => { setNewNodeData({...newNodeData, script: e.target.value}); markDirty(); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={4}
              placeholder="What will you say to the prospect at this point?"
            />
          </div>
                        
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tips & Coaching Notes</label>
            <textarea
              value={newNodeData.coaching || ''}
              onChange={(e) => { setNewNodeData({...newNodeData, coaching: e.target.value}); markDirty(); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Tips for handling this part of the conversation..."
            />
          </div>
                        
          <div className="flex space-x-2">
            <Button
              variant="primary"
              onClick={handleSaveNewNode}
            >
              Create Question
            </Button>
            <Button
              variant="outline"
              onClick={() => setViewMode('flow-connected')}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Lead form editing is now done on the Leads page */}

      {/* Flowchart Editor modal removed - now accessed via separate route */}

      {/* Revision History Side Panel */}
      {showRevisionHistory && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setShowRevisionHistory(false)}
            style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)',
              zIndex: 400,
            }}
          />
          {/* Panel */}
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0,
            width: '420px', maxWidth: '95vw',
            backgroundColor: 'white',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
            zIndex: 401,
            display: 'flex', flexDirection: 'column',
          }}>
            <RevisionHistory
              key={selectedTemplate}
              teamId={effectiveUserTeam}
              templateId={selectedTemplate}
              templateName={displayTemplates[selectedTemplate]?.name || selectedTemplate}
              currentGraph={localScriptGraph || {}}
              onRestore={(revision) => {
                if (onUpdateTemplate) {
                  const restoredData = {
                    graph: revision.graph || {},
                    steps: revision.steps || displayTemplates[selectedTemplate]?.steps,
                    prospectForm: revision.prospectForm || displayTemplates[selectedTemplate]?.prospectForm,
                  };
                  onUpdateTemplate(selectedTemplate, restoredData, displayTemplates[selectedTemplate]?.name, false, {
                    reason: 'After restore',
                    triggeredBy: 'restore',
                  });
                  setShowRevisionHistory(false);
                  toast.success('Restored to previous version');
                }
              }}
              onClose={() => setShowRevisionHistory(false)}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ScriptBuilder; 